import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { getAuth } from 'firebase/auth';
import NotificationService from './NotificationService';
import { collection, query, onSnapshot, doc, updateDoc, deleteDoc, getDocs, where, setDoc, getDoc, addDoc, arrayUnion, arrayRemove } from 'firebase/firestore';
import { db } from '../firebase';
import { Plus, BarChart3, FileText, Users, Calendar, Target, TrendingUp, Play, Clock, ChevronLeft, ChevronRight, ChevronDown, Archive, Trash2, XCircle, CheckCircle, MessageSquare, X } from 'lucide-react';
import './DashboardCampaigns.css';
import CreateCampaignModal from './CreateCampaignModal';
import CampaignCreatedModal from './CampaignCreatedModal';
import EditCampaignModal from './EditCampaignModal';
import BriefDetailModal from './BriefDetailModal';
import ScriptDetailModal from './ScriptDetailModal';
import ScriptFeedbackModal from './ScriptFeedbackModal';
import AddScriptToBriefModal from './AddScriptToBriefModal';
import ClientBriefPanel from './ClientBriefPanel';
import ClientScriptPanel from './ClientScriptPanel';
import CreatorProgressModal from './CreatorProgressModal';
import CancellationRequestModal from './CancellationRequestModal';
import AssignCreatorModal from './AssignCreatorModal';
import ApprovedContentModal from './ApprovedContentModal';
import BriefCreatedModal from './BriefCreatedModal';
import FindCreatorsPanel from './FindCreatorsPanel';
import CreditPurchaseModal from './CreditPurchaseModal';
import RejectProposalFeedbackModal from './RejectProposalFeedbackModal';
import CreatorModal from './CreatorModal';
import { useChat } from '../Chat/ChatContext';
import { ProjectIdGenerator } from '../utils/projectIdGenerator';
import { addBusinessDays } from '../utils/dateUtils';
import {
  getCreatorDueInfo,
  getCreatorStatusLabel,
  getCreatorStatusCssClass,
  ACCEPT_DEADLINE_BUSINESS_DAYS,
} from '../utils/creatorDueUtils';
import SubmissionService from '../services/submissionService';
import CreditService from '../services/creditService';

const TERMINAL_CREATOR_STATUSES = new Set(['awaiting_approval', 'rejected', 'completed', 'cancelled']);

function isApprovedProjectId(projectId, approvedProjectIds) {
  if (!projectId || !approvedProjectIds || approvedProjectIds.size === 0) return false;
  const normalized = ProjectIdGenerator.normalizeProjectId(projectId) || projectId;
  if (approvedProjectIds.has(normalized)) return true;
  for (const approvedId of approvedProjectIds) {
    try {
      if (ProjectIdGenerator.areEquivalent(approvedId, normalized)) return true;
    } catch (_) {
      // ignore comparison errors
    }
  }
  return false;
}

function isActiveCampaignCreator(creator, approvedProjectIds) {
  const status = creator?.status;
  if (!status || TERMINAL_CREATOR_STATUSES.has(status)) return false;
  if (isApprovedProjectId(creator.projectId, approvedProjectIds)) return false;
  return true;
}

function countActiveCreatorsInBriefs(briefs, approvedProjectIds) {
  return briefs.reduce((count, brief) => {
    if (!Array.isArray(brief.creators)) return count;
    return count + brief.creators.filter((creator) => isActiveCampaignCreator(creator, approvedProjectIds)).length;
  }, 0);
}

function DashboardCampaigns({ setActiveTab, setStartBriefForCampaign, autoOpenCreateModal, onModalOpened }) {
  const { getUnreadCount, getChatForProject } = useChat();

  // Campaign detail view is now inline instead of modal
  const [showCreateCampaignModal, setShowCreateCampaignModal] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showCongratsModal, setShowCongratsModal] = useState(false);
  const [justCreatedCampaign, setJustCreatedCampaign] = useState('');
  const [showEditCampaignModal, setShowEditCampaignModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  
  // Tooltip state
  const [tooltip, setTooltip] = useState({ visible: false, text: '', position: { top: 0, left: 0 } });
  
  // Bottom scroll state
  const [canScrollLeftBottom, setCanScrollLeftBottom] = useState(false);
  const [canScrollRightBottom, setCanScrollRightBottom] = useState(false);
  const [campaignBriefs, setCampaignBriefs] = useState([]);
  const [showBriefDetailModal, setShowBriefDetailModal] = useState(false);
  const [selectedBrief, setSelectedBrief] = useState(null);
  const [showScriptDetailModal, setShowScriptDetailModal] = useState(false);
  const [showScriptFeedbackModal, setShowScriptFeedbackModal] = useState(false);
  const [selectedScript, setSelectedScript] = useState(null);
  const [campaignSubmissions, setCampaignSubmissions] = useState([]);
  const [expandedCampaign, setExpandedCampaign] = useState(null);
  const [campaignFilter, setCampaignFilter] = useState('active'); // 'active' or 'archived'
  const [showArchiveConfirm, setShowArchiveConfirm] = useState(false);
  const [campaignToArchive, setCampaignToArchive] = useState(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const campaignsGridRef = useRef(null);
  const [canScrollLeftArchived, setCanScrollLeftArchived] = useState(false);
  const [canScrollRightArchived, setCanScrollRightArchived] = useState(false);
  const archivedCampaignsGridRef = useRef(null);
  const [canScrollLeftBriefs, setCanScrollLeftBriefs] = useState(false);
  const [canScrollRightBriefs, setCanScrollRightBriefs] = useState(false);
  const briefsGridRef = useRef(null);
  
  // Script scroll state
  const [canScrollLeftScripts, setCanScrollLeftScripts] = useState(false);
  const [canScrollRightScripts, setCanScrollRightScripts] = useState(false);
  const scriptsGridRef = useRef(null);
  
  // Creators scroll state
  const [canScrollLeftCreators, setCanScrollLeftCreators] = useState(false);
  const [canScrollRightCreators, setCanScrollRightCreators] = useState(false);
  const creatorsGridRef = useRef(null);
  
  // Section scroll refs
  const briefsSectionRef = useRef(null);
  const scriptsSectionRef = useRef(null);
  const creatorsSectionRef = useRef(null);
  
  // Track previous creator states for acceptance detection
  const previousCreatorsRef = useRef(new Map());
  const [showBriefPanel, setShowBriefPanel] = useState(false);
  const [showScriptPanel, setShowScriptPanel] = useState(false);
  const [briefsLoading, setBriefsLoading] = useState(true);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [campaignScripts, setCampaignScripts] = useState([]);
  const [scriptsLoading, setScriptsLoading] = useState(true);
  const [scriptBriefFilter, setScriptBriefFilter] = useState('all');
  const [scriptStatusFilter, setScriptStatusFilter] = useState('complete'); // 'complete' | 'drafts' | 'pending'
  const [showViewBriefPanel, setShowViewBriefPanel] = useState(false);
  const [viewBriefData, setViewBriefData] = useState(null);
  const [showViewScriptPanel, setShowViewScriptPanel] = useState(false);
  const [viewScriptData, setViewScriptData] = useState(null);
  const [showAddScriptToBriefModal, setShowAddScriptToBriefModal] = useState(false);
  const [showCreatorProgress, setShowCreatorProgress] = useState(false);
  const [selectedCreatorForProgress, setSelectedCreatorForProgress] = useState(null);
  const [showCancellationRequestModal, setShowCancellationRequestModal] = useState(false);
  const [selectedCreatorForCancellation, setSelectedCreatorForCancellation] = useState(null);
  const [campaignSortBy, setCampaignSortBy] = useState('recent');
  const [campaignShortlistedCreators, setCampaignShortlistedCreators] = useState([]);
  const [showAssignCreatorModal, setShowAssignCreatorModal] = useState(false);
  const [showApprovedModal, setShowApprovedModal] = useState(false);
  const [selectedApprovedSubmission, setSelectedApprovedSubmission] = useState(null);
  const [completedCreators, setCompletedCreators] = useState([]);
  const [showCreditPurchaseModal, setShowCreditPurchaseModal] = useState(false);
  const [currentCredits, setCurrentCredits] = useState(0);
  const [creatorToAssign, setCreatorToAssign] = useState(null);
  const [currentlyWorkingCreators, setCurrentlyWorkingCreators] = useState([]);
  const [creatorStatusFilter, setCreatorStatusFilter] = useState('all'); // 'all' | 'accepted' | 'awaiting_approval'
  const [showCreatorPanel, setShowCreatorPanel] = useState(false);
  const [selectedCreator, setSelectedCreator] = useState(null);
  const [showBriefCreatedModal, setShowBriefCreatedModal] = useState(false);
  const [justCreatedBriefName, setJustCreatedBriefName] = useState('');
  const [showRejectProposalModal, setShowRejectProposalModal] = useState(false);
  const [creatorToRejectProposal, setCreatorToRejectProposal] = useState(null);
  const [showProposedCreatorModal, setShowProposedCreatorModal] = useState(false);
  const [selectedProposedCreatorForModal, setSelectedProposedCreatorForModal] = useState(null);
  const [approvedSubmissionProjectIds, setApprovedSubmissionProjectIds] = useState(() => new Set());

  const briefSubscriptionKey = useMemo(
    () => campaignBriefs.map((brief) => `${brief.campaignId}:${brief.id}`).sort().join('|'),
    [campaignBriefs]
  );

  const countActiveCreatorsForCampaign = useCallback((campaignId) => {
    const briefsForCampaign = campaignBriefs.filter((brief) => brief.campaignId === campaignId);
    return countActiveCreatorsInBriefs(briefsForCampaign, approvedSubmissionProjectIds);
  }, [campaignBriefs, approvedSubmissionProjectIds]);

  useEffect(() => {
    if (autoOpenCreateModal) {
      setShowCreateCampaignModal(true);
      if (onModalOpened) onModalOpened();
    }
  }, [autoOpenCreateModal, onModalOpened]);

  // Fetch briefs from Firestore
  useEffect(() => {
    const auth = getAuth();
    const user = auth.currentUser;
    
    if (!user) {
      setBriefsLoading(false);
      return;
    }

    // Get all campaigns first, then fetch briefs for each campaign
    const fetchCampaignsAndBriefs = async () => {
      try {
        // Get user's campaigns
        const campaignsRef = collection(db, 'users', user.uid, 'campaigns');
        const campaignsSnapshot = await getDocs(campaignsRef);
        const campaigns = campaignsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        
        if (campaigns.length === 0) {
          setCampaignBriefs([]);
          setBriefsLoading(false);
          return;
        }

        // Fetch briefs for all campaigns
        const allBriefs = [];
        const unsubscribes = [];

        for (const campaign of campaigns) {
          const briefsRef = collection(db, 'users', user.uid, 'campaigns', campaign.id, 'briefs');
          const unsub = onSnapshot(briefsRef, (snapshot) => {
            const briefs = snapshot.docs.map(doc => ({ 
              id: doc.id, 
              ...doc.data(), 
              campaignId: campaign.id,
              campaignName: campaign.name 
            }));
            
            console.log(`=== BRIEFS FROM FIRESTORE ===`);
            console.log(`Campaign: ${campaign.name} (${campaign.id})`);
            console.log(`Briefs:`, briefs);
            console.log(`Brief names:`, briefs.map(b => b.name));
            
            // Update briefs for this campaign
            setCampaignBriefs(prev => {
              const filtered = prev.filter(b => b.campaignId !== campaign.id);
              const next = [...filtered, ...briefs];
              return next;
            });
          });
          unsubscribes.push(unsub);
        }

        setBriefsLoading(false);

        return () => {
          unsubscribes.forEach(unsub => {
            try {
              unsub();
            } catch (error) {
              console.error('Error unsubscribing from briefs:', error);
            }
          });
        };
        
      } catch (error) {
        console.error('Error fetching campaigns and briefs:', error);
        setCampaignBriefs([]);
        setBriefsLoading(false);
      }
    };

    const cleanup = fetchCampaignsAndBriefs();
    
    return () => {
      if (cleanup && typeof cleanup.then === 'function') {
        cleanup.then(cleanupFn => {
          if (cleanupFn) cleanupFn();
        });
      }
    };
  }, []);

  // Track approved submissions so campaign cards don't count finished projects as active
  useEffect(() => {
    const auth = getAuth();
    const user = auth.currentUser;
    if (!user || campaignBriefs.length === 0) {
      setApprovedSubmissionProjectIds(new Set());
      return undefined;
    }

    const perBriefApproved = new Map();
    const unsubscribes = campaignBriefs.map((brief) => {
      const briefKey = `${brief.campaignId}:${brief.id}`;
      const submissionsRef = collection(
        db,
        'users',
        user.uid,
        'campaigns',
        brief.campaignId,
        'briefs',
        brief.id,
        'submissions'
      );

      return onSnapshot(submissionsRef, (snapshot) => {
        const briefApproved = new Set();
        snapshot.docs.forEach((submissionDoc) => {
          const submissionData = submissionDoc.data();
          if (submissionData.status === 'approved' && submissionData.projectId) {
            briefApproved.add(
              ProjectIdGenerator.normalizeProjectId(submissionData.projectId) || submissionData.projectId
            );
          }
        });
        perBriefApproved.set(briefKey, briefApproved);

        const merged = new Set();
        perBriefApproved.forEach((ids) => ids.forEach((id) => merged.add(id)));
        setApprovedSubmissionProjectIds(merged);
      });
    });

    return () => unsubscribes.forEach((unsub) => unsub());
  }, [briefSubscriptionKey]);

  // Load current credits on mount
  useEffect(() => {
    const loadCredits = async () => {
      try {
        const credits = await CreditService.getBalance();
        setCurrentCredits(credits);
      } catch (error) {
        console.error('Error loading credits:', error);
      }
    };
    loadCredits();
  }, []);

  // Detect creator acceptance and create notifications
  useEffect(() => {
    const auth = getAuth();
    const user = auth.currentUser;
    if (!user || !campaignBriefs.length) {
      // Initialize previous state on first load
      if (campaignBriefs.length === 0) {
        previousCreatorsRef.current.clear();
      }
      return;
    }

    console.log('🔔 Checking for creator acceptance status changes...');

    // Check each brief for creator status changes
    campaignBriefs.forEach(brief => {
      if (!brief.creators || !Array.isArray(brief.creators)) return;

      brief.creators.forEach(creator => {
        // Create a unique key for this creator assignment
        const creatorKey = `${brief.campaignId}_${brief.id}_${creator.id}_${creator.projectId || ''}`;
        const previousStatus = previousCreatorsRef.current.get(creatorKey);
        const currentStatus = creator.status;

        console.log(`🔔 Creator ${creator.name || creator.handle || creator.id}:`, {
          creatorKey,
          previousStatus,
          currentStatus,
          hasPreviousStatus: previousStatus !== undefined
        });

        // Check if creator is confirmed/accepted and we haven't tracked them before
        // This handles cases where acceptance happened before page load
        const isAccepted = (currentStatus === 'confirmed' || currentStatus === 'accepted');
        const wasAccepted = (previousStatus === 'confirmed' || previousStatus === 'accepted');
        
        // Create notification if:
        // 1. Status changed from awaiting_approval to confirmed/accepted (real-time change)
        // 2. Creator is confirmed/accepted but we haven't seen them before AND they were recently assigned (within last 10 minutes)
        const shouldCreateNotification = 
          (previousStatus !== undefined && 
           previousStatus === 'awaiting_approval' && 
           isAccepted &&
           previousStatus !== currentStatus) ||
          (previousStatus === undefined && 
           isAccepted && 
           creator.assignedAt && 
           (Date.now() - new Date(creator.assignedAt).getTime()) < 10 * 60 * 1000 &&
           !wasAccepted);

        if (shouldCreateNotification) {
          console.log('✅ Creator acceptance detected! Creating notification...', {
            creatorId: creator.id,
            creatorName: creator.name || creator.handle || 'Creator',
            briefId: brief.id,
            briefTitle: brief.title || brief.name,
            previousStatus,
            currentStatus,
            assignedAt: creator.assignedAt
          });

          // Create notification
          const assignmentData = {
            campaignId: brief.campaignId,
            campaignName: brief.campaignName || campaigns.find(c => c.id === brief.campaignId)?.name || 'Campaign',
            briefId: brief.id,
            briefTitle: brief.title || brief.name || 'Untitled Brief',
            briefName: brief.title || brief.name || 'Untitled Brief',
            scriptIds: creator.scriptIds || [],
            productType: creator.productType || 'service',
            projectId: creator.projectId
          };

          NotificationService.notifyBrandCreatorAccepted(
            creator.id,
            creator.name || creator.handle || 'Creator',
            assignmentData,
            user.uid
          ).then((result) => {
            if (result) {
              console.log('✅ Notification created successfully');
            } else {
              console.log('ℹ️ Notification already exists, skipped');
            }
          }).catch(error => {
            console.error('❌ Error creating acceptance notification:', error);
          });
        }

        // Update the previous status (even if undefined, to track it for next time)
        previousCreatorsRef.current.set(creatorKey, currentStatus);
      });
    });
  }, [campaignBriefs, campaigns]);

  // Fetch scripts from Firestore
  useEffect(() => {
    const auth = getAuth();
    const user = auth.currentUser;
    
    if (!user) {
      setScriptsLoading(false);
      return;
    }

    // Get all campaigns first, then fetch scripts for each campaign
    const fetchCampaignsAndScripts = async () => {
      try {
        // Get user's campaigns
        const campaignsRef = collection(db, 'users', user.uid, 'campaigns');
        const campaignsSnapshot = await getDocs(campaignsRef);
        const campaigns = campaignsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        
        if (campaigns.length === 0) {
          setCampaignScripts([]);
          setScriptsLoading(false);
          return;
        }

        // Fetch scripts for all campaigns
        const allScripts = [];
        const unsubscribes = [];

        for (const campaign of campaigns) {
          const briefsRef = collection(db, 'users', user.uid, 'campaigns', campaign.id, 'briefs');
          const unsub = onSnapshot(briefsRef, (snapshot) => {
            const briefs = snapshot.docs.map(doc => ({ 
              id: doc.id, 
              ...doc.data(), 
              campaignId: campaign.id,
              campaignName: campaign.name 
            }));
            
            // Extract scripts from all briefs in this campaign
            const campaignScripts = briefs.flatMap(brief => 
              (brief.scripts || [])
                .map(script => ({
                  ...script,
                  briefId: brief.id,
                  briefTitle: brief.title || brief.name,
                  campaignId: campaign.id,
                  campaignName: campaign.name,
                  // Add default tag if none exists
                  tag: script.tag || (() => {
                    const title = script.name || script.title || '';
                    if (title.toLowerCase().includes('tutorial')) return 'Tutorial';
                    if (title.toLowerCase().includes('review')) return 'Review';
                    if (title.toLowerCase().includes('unboxing')) return 'Unboxing';
                    if (title.toLowerCase().includes('ad')) return 'Ad';
                    if (title.toLowerCase().includes('story')) return 'Story';
                    if (title.toLowerCase().includes('day')) return 'Day in the Life';
                    return 'Ad'; // Default fallback
                  })()
                }))
            );
            
            console.log(`=== SCRIPTS FROM FIRESTORE ===`);
            console.log(`Campaign: ${campaign.name} (${campaign.id})`);
            console.log(`Scripts:`, campaignScripts);
            console.log(`Scripts with tags:`, campaignScripts.map(s => ({ name: s.name, tag: s.tag })));
            
            // Update scripts for this campaign
            setCampaignScripts(prev => {
              const filtered = prev.filter(s => s.campaignId !== campaign.id);
              return [...filtered, ...campaignScripts];
            });
          });
          unsubscribes.push(unsub);
        }

        setScriptsLoading(false);

        return () => {
          unsubscribes.forEach(unsub => {
            try {
              unsub();
            } catch (error) {
              console.error('Error unsubscribing from scripts:', error);
            }
          });
        };
        
      } catch (error) {
        console.error('Error fetching campaigns and scripts:', error);
        setCampaignScripts([]);
        setScriptsLoading(false);
      }
    };

    const cleanup = fetchCampaignsAndScripts();
    
    return () => {
      if (cleanup && typeof cleanup.then === 'function') {
        cleanup.then(cleanupFn => {
          if (cleanupFn) cleanupFn();
        });
      }
    };
  }, []);

  useEffect(() => {
    const auth = getAuth();
    const user = auth.currentUser;
    if (!user) return;
    const campaignsRef = collection(db, 'users', user.uid, 'campaigns');
    const q = query(campaignsRef);
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const campaignsList = [];
      snapshot.forEach((doc) => {
        campaignsList.push({ id: doc.id, ...doc.data() });
      });
      
      setCampaigns(campaignsList);
      setIsLoading(false);
    }, (error) => {
      console.error('Error fetching campaigns:', error);
      setIsLoading(false);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    checkScrollButtons();
    checkScrollButtonsArchived();
    checkBottomScrollButtons();
    checkBriefScrollButtons();
    checkScriptScrollButtons();
    checkCreatorsScrollButtons();
    const handleResize = () => {
      checkScrollButtons();
      checkScrollButtonsArchived();
      checkBottomScrollButtons();
      checkBriefScrollButtons();
      checkScriptScrollButtons();
      checkCreatorsScrollButtons();
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [campaigns, campaignFilter, campaignBriefs, campaignScripts, currentlyWorkingCreators, creatorStatusFilter, expandedCampaign]);

  // Fetch shortlisted creators for the expanded campaign
  useEffect(() => {
    if (!expandedCampaign) {
      setCampaignShortlistedCreators([]);
      setCurrentlyWorkingCreators([]);
      return;
    }

    const auth = getAuth();
    const user = auth.currentUser;
    if (!user) return;

    const campaignRef = doc(db, 'users', user.uid, 'campaigns', expandedCampaign.id);
    const unsubscribe = onSnapshot(campaignRef, (doc) => {
      if (doc.exists()) {
        const campaignData = doc.data();
        const creators = campaignData.creators || [];
        setCampaignShortlistedCreators(creators);
      } else {
        setCampaignShortlistedCreators([]);
      }
    });

    return () => unsubscribe();
  }, [expandedCampaign]);

  useEffect(() => {
    if (!expandedCampaign) {
      setScriptBriefFilter('all');
      setScriptStatusFilter('complete');
      return;
    }
    const briefsForCampaign = campaignBriefs.filter(b => b.campaignId === expandedCampaign.id);
    const briefIds = briefsForCampaign.map(b => b.id);
    if (scriptBriefFilter !== 'all' && !briefIds.includes(scriptBriefFilter)) {
      setScriptBriefFilter('all');
    }
    const hasDraftsForCampaign = campaignScripts.some(
      script => script.campaignId === expandedCampaign.id && script.isDraft
    );
    const hasPendingForCampaign = campaignScripts.some(
      s => s.campaignId === expandedCampaign.id && s.approvalStatus === 'pending_brand_approval' && !s.isDraft
    );
    if (!hasDraftsForCampaign && scriptStatusFilter === 'drafts') {
      setScriptStatusFilter('complete');
    }
    if (!hasPendingForCampaign && scriptStatusFilter === 'pending') {
      setScriptStatusFilter('complete');
    }
  }, [expandedCampaign, campaignBriefs, campaignScripts, scriptBriefFilter, scriptStatusFilter]);

  // Fetch currently working creators (assigned to briefs)
  useEffect(() => {
    if (!expandedCampaign) {
      setCurrentlyWorkingCreators([]);
      setCreatorStatusFilter('all'); // Reset filter when campaign is closed
      return;
    }

    const auth = getAuth();
    const user = auth.currentUser;
    if (!user) return;

    // Track submission listeners for cleanup
    const submissionUnsubscribes = new Map(); // Map briefId -> unsubscribe function

    // Use real-time listener for automatic updates when creator accepts and provides shipping details
    // This listener is the source of truth for creator data including shipping details
        const briefsRef = collection(db, 'users', user.uid, 'campaigns', expandedCampaign.id, 'briefs');
    const unsubscribe = onSnapshot(briefsRef, async (briefsSnapshot) => {
        console.log('📋 Briefs snapshot updated - refreshing all creator data including shipping details');
        const workingCreators = [];
        const currentBriefIds = new Set(briefsSnapshot.docs.map(doc => doc.id));

        // Clean up listeners for briefs that no longer exist
        for (const [briefId, unsubscribeFn] of submissionUnsubscribes) {
          if (!currentBriefIds.has(briefId)) {
            unsubscribeFn();
            submissionUnsubscribes.delete(briefId);
          }
        }

          // Process each brief and its creators
          for (const briefDoc of briefsSnapshot.docs) {
          const briefData = briefDoc.data();
          const briefCreators = briefData.creators || [];
          // Try to infer product/service from brief scripts (fallback to 'service')
          const firstScript = (briefData.scripts && briefData.scripts.length > 0) ? briefData.scripts[0] : null;
          const productType = (firstScript && firstScript.settings && firstScript.settings.productType) 
            ? String(firstScript.settings.productType).toLowerCase() 
            : 'service';
          
          // Check for submissions in this brief - use real-time listener
          // Use ONLY projectId for matching (no creatorId matching to avoid conflicts)
          let submissionsMap = new Map(); // Map: normalizedProjectId -> submissionId
          try {
            const submissionsRef = collection(
              db,
              'users',
              user.uid,
              'campaigns',
              expandedCampaign.id,
              'briefs',
              briefDoc.id,
              'submissions'
            );
            
            // Set up real-time listener for submissions if not already set up
            if (!submissionUnsubscribes.has(briefDoc.id)) {
              const submissionsUnsubscribe = onSnapshot(submissionsRef, (submissionsSnapshot) => {
                // Rebuild submissions map when submissions change - use ONLY projectId for matching
                const newSubmissionsMap = new Map(); // Map: normalizedProjectId -> { submissionId, submittedAt, dueDate, status }
                submissionsSnapshot.docs.forEach(submissionDoc => {
                  const submissionData = submissionDoc.data();
                  const submissionProjectId = submissionData.projectId ? 
                    (ProjectIdGenerator.normalizeProjectId(submissionData.projectId) || submissionData.projectId) : null;
                  
                  // Only store submissions with projectId - projectId is required for matching
                  if (submissionProjectId) {
                    const submittedAt = submissionData.submittedAt?.toDate ? 
                      submissionData.submittedAt.toDate() : 
                      (submissionData.submittedAt instanceof Date ? 
                        submissionData.submittedAt : 
                        (submissionData.submittedAt ? new Date(submissionData.submittedAt) : null));
                    
                    const dueDate = submissionData.dueDate?.toDate ? 
                      submissionData.dueDate.toDate() : 
                      (submissionData.dueDate instanceof Date ? 
                        submissionData.dueDate : 
                        (submissionData.dueDate ? new Date(submissionData.dueDate) : null));
                    
                    // Use normalized projectId as the key (no creatorId needed)
                    newSubmissionsMap.set(submissionProjectId, {
                      submissionId: submissionDoc.id,
                      submittedAt: submittedAt,
                      dueDate: dueDate || (submittedAt ? addBusinessDays(submittedAt, 5) : null),
                      status: submissionData.status,
                      lastRevisionRequestedAt: submissionData.lastRevisionRequestedAt || null
                    });
                  } else {
                    console.warn(`⚠️ Submission ${submissionDoc.id} missing projectId - cannot match to creator`);
                  }
                });
                
                // Update working creators with new submission status
                setCurrentlyWorkingCreators(prevCreators => {
                  // Filter out creators with approved submissions and update others
                  return prevCreators
                    .map(creator => {
                      // Only update creators from this brief
                      if (creator.briefId !== briefDoc.id) {
                        return creator;
                      }
                      
                      // Match ONLY by projectId - no creatorId matching
                      let hasSubmission = false;
                      let submissionId = null;
                      let submissionData = null;
                      const normalizedProjectId = creator.projectId;
                      
                      if (normalizedProjectId) {
                        // Try exact match with normalized projectId
                        if (newSubmissionsMap.has(normalizedProjectId)) {
                          submissionData = newSubmissionsMap.get(normalizedProjectId);
                          hasSubmission = true;
                          submissionId = submissionData.submissionId;
                        } else {
                          // Try matching by projectId equivalence (handles variations in formatting)
                          for (const [submissionProjectId, subData] of newSubmissionsMap) {
                            try {
                              if (ProjectIdGenerator.areEquivalent(submissionProjectId, normalizedProjectId)) {
                                submissionData = subData;
                                hasSubmission = true;
                                submissionId = subData.submissionId;
                                break;
                              }
                            } catch (e) {
                              // If comparison fails, continue
                            }
                          }
                        }
                      } else {
                        // No projectId on creator - cannot match (old system fallback removed)
                        console.warn(`⚠️ Creator ${creator.name || creator.handle} (${creator.id}) missing projectId - cannot match to submission`);
                      }
                      
                      // Only update submission-related fields
                      // Shipping details come from the briefs listener (source of truth)
                      // Don't modify deliveryAddress here - it's updated by the briefs listener
                      return {
                        ...creator,
                        hasSubmission: hasSubmission,
                        submissionId: submissionId,
                        submissionData: submissionData
                        // Note: deliveryAddress is NOT updated here - it comes from briefs listener
                      };
                    })
                    .filter(creator => {
                      // Remove creators with approved submissions - they should be in completed section
                      // (unless cancelled — those stay for refund handling, not Completed)
                      if (
                        creator.submissionData &&
                        creator.submissionData.status === 'approved' &&
                        creator.status !== 'cancelled' &&
                        !creator.cancelledAt
                      ) {
                        return false;
                      }
                      // Remove creators with completed status
                      if (creator.status === 'completed') {
                        return false;
                      }
                      return true;
                    });
                });
              });
              
              submissionUnsubscribes.set(briefDoc.id, submissionsUnsubscribe);
            }
            
            // Initial fetch to populate submissions map synchronously for first render
            const initialSnapshot = await getDocs(submissionsRef);
            console.log(`📋 Found ${initialSnapshot.docs.length} submissions for brief ${briefDoc.id}`);
            initialSnapshot.docs.forEach(submissionDoc => {
              const submissionData = submissionDoc.data();
              const submissionProjectId = submissionData.projectId ? 
                (ProjectIdGenerator.normalizeProjectId(submissionData.projectId) || submissionData.projectId) : null;
              
              console.log(`📄 Submission ${submissionDoc.id}:`, {
                creatorId: submissionData.creatorId,
                projectId: submissionProjectId,
                status: submissionData.status,
                briefId: briefDoc.id
              });
              
              // Store submission data by projectId ONLY (no creatorId in key)
              if (submissionProjectId) {
                const submittedAt = submissionData.submittedAt?.toDate ? 
                  submissionData.submittedAt.toDate() : 
                  (submissionData.submittedAt instanceof Date ? 
                    submissionData.submittedAt : 
                    (submissionData.submittedAt ? new Date(submissionData.submittedAt) : null));
                
                const dueDate = submissionData.dueDate?.toDate ? 
                  submissionData.dueDate.toDate() : 
                  (submissionData.dueDate instanceof Date ? 
                    submissionData.dueDate : 
                    (submissionData.dueDate ? new Date(submissionData.dueDate) : null));
                
                submissionsMap.set(submissionProjectId, {
                  submissionId: submissionDoc.id,
                  submittedAt: submittedAt,
                  dueDate: dueDate || (submittedAt ? addBusinessDays(submittedAt, 5) : null),
                  status: submissionData.status,
                  lastRevisionRequestedAt: submissionData.lastRevisionRequestedAt || null
                });
              } else {
                console.warn(`⚠️ Submission ${submissionDoc.id} missing projectId - cannot match to creator`);
              }
            });
          } catch (error) {
            console.error('Error checking for submissions:', error);
          }
          
          console.log(`📋 Brief "${briefData.title || briefData.name || briefDoc.id}" has ${briefCreators.length} creator entry/entries`);
          
          for (const creator of briefCreators) {
            const creatorStatus = creator.status;
            const rawProjectId = creator.projectId || creator.progress?.projectId || null;
            
            console.log('🔍 Processing creator entry from brief:', {
              name: creator.name || creator.handle,
              id: creator.id,
              status: creatorStatus,
              projectId: rawProjectId || 'MISSING',
              assignedAt: creator.assignedAt || 'N/A',
              acceptedAt: creator.acceptedAt || 'N/A'
            });
            
            console.log('🔍 Raw creator data from Firestore:', {
              name: creator.name,
              progress: creator.progress,
              deliveryAddress: creator.deliveryAddress,
                progressDeliveryAddress: creator.progress?.deliveryAddress,
                shippingDetails: creator.shippingDetails
              });
              
              // Check all possible locations for shipping details
              const deliveryAddress = creator.deliveryAddress 
                || creator.progress?.deliveryAddress 
                || (creator.shippingDetails ? {
                  addressLine1: creator.shippingDetails.addressLine1,
                  addressLine2: creator.shippingDetails.addressLine2 || '',
                  city: creator.shippingDetails.city,
                  state: creator.shippingDetails.state || '',
                  postalCode: creator.shippingDetails.postalCode,
                  country: creator.shippingDetails.country,
                  contactPerson: creator.shippingDetails.contactPerson || creator.name,
                  phone: creator.shippingDetails.phone || '',
                  specialInstructions: creator.shippingDetails.specialInstructions || ''
                } : null);
              
              // rawProjectId already declared above (line 688)
              const normalizedProjectId = ProjectIdGenerator.normalizeProjectId(rawProjectId) || rawProjectId || null;
              
              // Check if this creator has a submission - match ONLY by projectId
              // This prevents conflicts when same brief and creator are used multiple times
              let hasSubmission = false;
              let submissionId = null;
              let submissionData = null;
              
              if (normalizedProjectId) {
                // Try exact match with normalized projectId
                if (submissionsMap.has(normalizedProjectId)) {
                  submissionData = submissionsMap.get(normalizedProjectId);
                  hasSubmission = true;
                  submissionId = submissionData.submissionId;
                } else {
                  // Try matching by projectId equivalence (handles variations in formatting)
                  for (const [submissionProjectId, subData] of submissionsMap) {
                    try {
                      if (ProjectIdGenerator.areEquivalent(submissionProjectId, normalizedProjectId)) {
                        submissionData = subData;
                        hasSubmission = true;
                        submissionId = subData.submissionId;
                        break;
                      }
                    } catch (e) {
                      // If comparison fails, continue
                    }
                  }
                }
              } else {
                // No projectId on creator - cannot match (old system fallback removed)
                console.warn(`⚠️ Creator ${creator.name || creator.handle} (${creator.id}) missing projectId - cannot match to submission`);
              }
              
              // Log for debugging
              if (hasSubmission) {
                console.log(`✅ Creator entry matched to submission:`, {
                  creatorName: creator.name || creator.handle,
                  creatorId: creator.id,
                  projectId: normalizedProjectId,
                  submissionId: submissionId,
                  submissionStatus: submissionData?.status,
                  submissionProjectId: submissionData?.projectId || 'N/A'
                });
              } else {
                console.log(`ℹ️ Creator entry has NO matching submission:`, {
                  creatorName: creator.name || creator.handle,
                  creatorId: creator.id,
                  projectId: normalizedProjectId || 'MISSING',
                  status: creatorStatus,
                  availableSubmissionProjectIds: Array.from(submissionsMap.keys())
                });
              }
              
        // Skip creators that should be in completed section
              // IMPORTANT: Only skip if THIS creator entry's submission is approved AND not cancelled
              if (
                hasSubmission &&
                submissionData &&
                submissionData.status === 'approved' &&
                creatorStatus !== 'cancelled'
              ) {
                console.log(`🚫 Skipping OLD creator entry (completed project):`, {
                  creatorName: creator.name || creator.handle,
                  projectId: normalizedProjectId,
                  submissionStatus: submissionData.status
                });
                continue; // Skip this creator entry - it's from a completed project, continue to next entry
              }
              
              // Skip creators with completed status
              if (creatorStatus === 'completed') {
                console.log(`🚫 Skipping creator ${creator.name || creator.handle} - status completed (projectId: ${normalizedProjectId || 'none'})`);
                continue; // Skip this creator - it's completed, continue to next entry
              }
              
              // If creator has no submission, it's a NEW assignment - show it
              if (!hasSubmission) {
                console.log(`✅ Including NEW creator entry (no submission yet):`, {
                  creatorName: creator.name || creator.handle,
                  projectId: normalizedProjectId || 'MISSING (will be set when creator accepts)',
                  status: creatorStatus
                });
              }
              
              // For new assignments, projectId might not be set yet until creator accepts
              // But we still want to show them if they're confirmed/accepted/awaiting_approval
              // Only skip if they have a submission but no projectId (data inconsistency)
              if (!normalizedProjectId && hasSubmission) {
                console.log(`⚠️ Creator ${creator.name || creator.handle} has submission but missing projectId - cannot match properly, skipping`);
                continue; // Skip - data inconsistency, continue to next entry
              }
              
              // Log if projectId is missing for new assignments (for debugging)
              if (!normalizedProjectId) {
                console.log(`ℹ️ Creator ${creator.name || creator.handle} has no projectId yet (status: ${creatorStatus}) - will show but may need projectId for proper matching`);
              }
              
              console.log(`✅ Adding creator to working list: ${creator.name || creator.handle} (status: ${creatorStatus}, projectId: ${normalizedProjectId || 'none'})`);
              
              workingCreators.push({
                ...creator,
                projectId: normalizedProjectId,
                briefId: briefDoc.id,
                briefTitle: briefData.title || briefData.name,
                status: creatorStatus,
                productType,
                deliveryAddress: deliveryAddress,
                hasSubmission: hasSubmission,
                submissionId: submissionId,
                submissionData: submissionData,
                // Explicitly include deadline, extendedDeadline, graceRequest, and cancellationRequest to ensure they're preserved
                deadline: creator.deadline || null,
                extendedDeadline: creator.extendedDeadline || null,
                graceRequest: creator.graceRequest || null,
                cancellationRequest: creator.cancellationRequest || null
              });
            }
          }

        // Filter out creators with approved submissions or completed status - they should go to completed section
        // Also ensure we don't show duplicate creators (same creatorId + briefId but different projectId)
        const activeCreators = workingCreators.filter(creator => {
          // Exclude creators whose submissions are approved
          if (creator.submissionData && creator.submissionData.status === 'approved') {
            console.log(`🚫 Excluding creator ${creator.name || creator.handle} - submission approved (projectId: ${creator.projectId || 'none'})`);
            return false;
          }
          // Exclude creators with completed status
          if (creator.status === 'completed') {
            console.log(`🚫 Excluding creator ${creator.name || creator.handle} - status completed (projectId: ${creator.projectId || 'none'})`);
            return false;
          }
          // Only require projectId if they have a submission (to match correctly)
          // New assignments without projectId yet should still be shown
          if (!creator.projectId && creator.hasSubmission) {
            console.log(`⚠️ Creator ${creator.name || creator.handle} has submission but missing projectId - may cause matching issues`);
            // Still include them but log a warning
          }
          return true;
        });
        
        // Remove duplicates - if same creatorId + briefId + projectId appears multiple times, keep only the one with the most recent status
        // Use projectId in key to distinguish between multiple projects for same creator/brief
        const uniqueCreators = new Map();
        activeCreators.forEach(creator => {
          // Use projectId in key - if null/undefined, use 'none' to avoid issues
          const projectIdKey = creator.projectId || 'none';
          const key = `${creator.id}_${creator.briefId}_${projectIdKey}`;
          
          console.log(`🔑 Checking duplicate for key: ${key}`, {
            creatorName: creator.name || creator.handle,
            projectId: creator.projectId || 'MISSING',
            status: creator.status,
            hasSubmission: creator.hasSubmission
          });
          
          if (!uniqueCreators.has(key)) {
            uniqueCreators.set(key, creator);
            console.log(`✅ Added new unique creator: ${creator.name || creator.handle} (projectId: ${creator.projectId || 'MISSING'})`);
          } else {
            // If duplicate found (same creatorId + briefId + projectId), keep the one with the most recent activity
            const existing = uniqueCreators.get(key);
            const existingDate = existing.acceptedAt || existing.assignedAt || 0;
            const newDate = creator.acceptedAt || creator.assignedAt || 0;
            if (newDate > existingDate) {
              console.log(`🔄 Replacing duplicate creator (newer date): ${creator.name || creator.handle} (projectId: ${creator.projectId || 'MISSING'})`);
              uniqueCreators.set(key, creator);
            } else {
              console.log(`⏭️ Keeping existing creator (older date): ${existing.name || existing.handle} (projectId: ${existing.projectId || 'MISSING'})`);
            }
          }
        });
        
        const finalActiveCreators = Array.from(uniqueCreators.values());
        console.log(`✅ Final result: ${finalActiveCreators.length} active creators`, {
          fromWorkingCreators: workingCreators.length,
          afterStatusFilter: activeCreators.length,
          afterDuplicateRemoval: finalActiveCreators.length,
          finalCreators: finalActiveCreators.map(c => ({
            name: c.name || c.handle,
            projectId: c.projectId || 'MISSING',
            status: c.status
          }))
        });
        
        setCurrentlyWorkingCreators(finalActiveCreators);
    }, (error) => {
      console.error('Error listening to briefs:', error);
        setCurrentlyWorkingCreators([]);
    });

    return () => {
      unsubscribe();
      // Clean up all submission listeners
      for (const unsubscribeFn of submissionUnsubscribes.values()) {
        unsubscribeFn();
      }
      submissionUnsubscribes.clear();
    };
  }, [expandedCampaign]); // Real-time updates, no need for campaignBriefs dependency

  // Fetch completed creators with their approved videos - real-time updates
  useEffect(() => {
    if (!expandedCampaign) {
      setCompletedCreators([]);
      return;
    }

    const auth = getAuth();
    const user = auth.currentUser;
    if (!user) {
      setCompletedCreators([]);
      return;
    }

    // Track submission listeners for cleanup
    const submissionUnsubscribes = new Map(); // Map briefId -> unsubscribe function

    // Fetch all briefs for this campaign with real-time listener
    const briefsRef = collection(db, 'users', user.uid, 'campaigns', expandedCampaign.id, 'briefs');
    const unsubscribeBriefs = onSnapshot(briefsRef, async (briefsSnapshot) => {
      const completedCreatorsList = [];
      const processedProjectIds = new Set(); // Track processed creators to avoid duplicates
      const currentBriefIds = new Set(briefsSnapshot.docs.map(doc => doc.id));

      // Clean up listeners for briefs that no longer exist
      for (const [briefId, unsubscribeFn] of submissionUnsubscribes) {
        if (!currentBriefIds.has(briefId)) {
          unsubscribeFn();
          submissionUnsubscribes.delete(briefId);
        }
      }

      // Process each brief and set up real-time listeners for submissions
      for (const briefDoc of briefsSnapshot.docs) {
        const briefData = briefDoc.data();
        const briefCreators = briefData.creators || [];

        // Set up real-time listener for submissions in this brief
        if (!submissionUnsubscribes.has(briefDoc.id)) {
          const submissionsRef = collection(
            db,
            'users',
            user.uid,
            'campaigns',
            expandedCampaign.id,
            'briefs',
            briefDoc.id,
            'submissions'
          );
          
          const unsubscribeSubmissions = onSnapshot(submissionsRef, async (submissionsSnapshot) => {
            // Rebuild completed creators list when submissions change
            const newCompletedCreatorsList = [];
            const newProcessedProjectIds = new Set();

            console.log('🔄 Rebuilding completed creators list (submissions changed)');

            // Process all briefs again to get complete picture
            const allBriefsSnapshot = await getDocs(briefsRef);
            
            for (const currentBriefDoc of allBriefsSnapshot.docs) {
              const currentBriefData = currentBriefDoc.data();
              const currentBriefCreators = currentBriefData.creators || [];
              
              console.log(`📋 Processing brief "${currentBriefData.title || currentBriefDoc.id}" with ${currentBriefCreators.length} creator entries`);

              // Get submissions for this brief
              const currentSubmissionsRef = collection(
                db,
                'users',
                user.uid,
                'campaigns',
                expandedCampaign.id,
                'briefs',
                currentBriefDoc.id,
                'submissions'
              );
              
              const currentSubmissionsSnapshot = await getDocs(currentSubmissionsRef);
              
              console.log(`📄 Found ${currentSubmissionsSnapshot.docs.length} submissions for brief "${currentBriefData.title || currentBriefDoc.id}"`);
              
              for (const submissionDoc of currentSubmissionsSnapshot.docs) {
                const submissionData = submissionDoc.data();
                
                // Only process approved submissions
                if (submissionData.status !== 'approved') {
                  console.log(`⏭️ Skipping submission ${submissionDoc.id} - status: ${submissionData.status}`);
                  continue;
                }
                
                const submissionProjectId = submissionData.projectId ? 
                  (ProjectIdGenerator.normalizeProjectId(submissionData.projectId) || submissionData.projectId) : null;
                
                if (!submissionProjectId) {
                  console.warn(`⚠️ Submission ${submissionDoc.id} missing projectId`);
                  continue;
                }
                
                console.log(`🔍 Looking for creator entry matching submission projectId: ${submissionProjectId}`);
                
                // Find the creator that matches this submission by projectId
                // CRITICAL: Must match by projectId only - don't filter by status
                // Old completed projects should still be in the list
                let matchedCreator = null;
                for (const creator of currentBriefCreators) {
                  const rawProjectId = creator.projectId || creator.progress?.projectId || null;
                  const normalizedProjectId = ProjectIdGenerator.normalizeProjectId(rawProjectId) || rawProjectId || null;
                  
                  // Match by projectId ONLY - ignore status, ignore everything else
                  let matches = false;
                  if (normalizedProjectId) {
                    if (normalizedProjectId === submissionProjectId) {
                      matches = true;
                    } else {
                      try {
                        matches = ProjectIdGenerator.areEquivalent(normalizedProjectId, submissionProjectId);
                      } catch (e) {
                        // If comparison fails, continue
                      }
                    }
                  }
                  
                  if (matches) {
                    // Cancelled assignments must never appear in Completed
                    if (creator.status === 'cancelled' || creator.cancelledAt) {
                      console.log(`🚫 Skipping cancelled creator for completed list: projectId ${submissionProjectId}`);
                      continue;
                    }
                    matchedCreator = creator;
                    console.log(`✅ Found matching creator entry for projectId ${submissionProjectId} (creator: ${creator.name || creator.handle}, status: ${creator.status || 'N/A'})`);
                    break; // Found match, stop searching
                  }
                }
                
                if (matchedCreator && !newProcessedProjectIds.has(submissionProjectId)) {
                  newProcessedProjectIds.add(submissionProjectId);
                  const normalizedProjectId = ProjectIdGenerator.normalizeProjectId(matchedCreator.projectId || matchedCreator.progress?.projectId) || submissionProjectId;
                    
                  // Get approved videos from submission
                  const approvedVideos = submissionData.content || [];
                  const completedVideos = approvedVideos
                    .filter(video => video.status === 'approved')
                    .map(video => ({
                      id: video.id || video.videoId,
                      title: video.title || video.description || 'Untitled Video',
                      url: video.url || video.videoUrl,
                      thumbnail: video.thumbnail || video.thumbnailUrl,
                      duration: video.duration || 0
                    }));
                  
                  if (completedVideos.length > 0) {
                    console.log(`✅ Adding completed project to list: projectId ${normalizedProjectId}, ${completedVideos.length} videos`);
                    newCompletedCreatorsList.push({
                      id: matchedCreator.id,
                      name: matchedCreator.name || matchedCreator.handle || 'Unknown Creator',
                      handle: matchedCreator.handle || `@${matchedCreator.name?.toLowerCase().replace(/\s+/g, '')}` || '@unknown',
                      avatar: matchedCreator.profileImage || matchedCreator.avatar || '/creatorimages/creatorpic1v2-min.png',
                      profileImage: matchedCreator.profileImage || matchedCreator.avatar,
                      briefId: currentBriefDoc.id,
                      briefTitle: currentBriefData.title || currentBriefData.name,
                      projectId: normalizedProjectId,
                      completedVideos: completedVideos,
                      approvedAt: submissionData.submittedAt?.toDate ? 
                        submissionData.submittedAt.toDate() : 
                        (submissionData.submittedAt instanceof Date ? 
                          submissionData.submittedAt : 
                          new Date())
                    });
                  } else {
                    console.warn(`⚠️ Submission ${submissionDoc.id} has no approved videos`);
                  }
                } else if (!matchedCreator) {
                  console.warn(`⚠️ No creator entry found for submission projectId: ${submissionProjectId}`);
                } else if (newProcessedProjectIds.has(submissionProjectId)) {
                  console.log(`⏭️ Already processed projectId: ${submissionProjectId}`);
                }
              }
            }

            console.log(`✅ Completed creators list rebuilt: ${newCompletedCreatorsList.length} projects`);
            setCompletedCreators(newCompletedCreatorsList);
          });
          
          submissionUnsubscribes.set(briefDoc.id, unsubscribeSubmissions);
        }

        // Initial fetch for this brief
        try {
          const submissionsRef = collection(
            db,
            'users',
            user.uid,
            'campaigns',
            expandedCampaign.id,
            'briefs',
            briefDoc.id,
            'submissions'
          );
          
          const submissionsSnapshot = await getDocs(submissionsRef);
          
          for (const submissionDoc of submissionsSnapshot.docs) {
            const submissionData = submissionDoc.data();
            
            // Only process approved submissions
            if (submissionData.status !== 'approved') continue;
            
            const submissionProjectId = submissionData.projectId ? 
              (ProjectIdGenerator.normalizeProjectId(submissionData.projectId) || submissionData.projectId) : null;
            
            if (!submissionProjectId || processedProjectIds.has(submissionProjectId)) continue;
            
            // Find the creator that matches this submission
            for (const creator of briefCreators) {
              const rawProjectId = creator.projectId || creator.progress?.projectId || null;
              const normalizedProjectId = ProjectIdGenerator.normalizeProjectId(rawProjectId) || rawProjectId || null;
              
              // Match by projectId
              let matches = false;
              if (normalizedProjectId) {
                if (normalizedProjectId === submissionProjectId) {
                  matches = true;
                } else {
                  try {
                    matches = ProjectIdGenerator.areEquivalent(normalizedProjectId, submissionProjectId);
                  } catch (e) {
                    // If comparison fails, continue
                  }
                }
              }
              
              if (matches) {
                // Cancelled assignments must never appear in Completed
                if (creator.status === 'cancelled' || creator.cancelledAt) {
                  break;
                }
                processedProjectIds.add(normalizedProjectId);
                
                // Get approved videos from submission
                const approvedVideos = submissionData.content || [];
                const completedVideos = approvedVideos
                  .filter(video => video.status === 'approved')
                  .map(video => ({
                    id: video.id || video.videoId,
                    title: video.title || video.description || 'Untitled Video',
                    url: video.url || video.videoUrl,
                    thumbnail: video.thumbnail || video.thumbnailUrl,
                    duration: video.duration || 0
                  }));
                
                if (completedVideos.length > 0) {
                  completedCreatorsList.push({
                    id: creator.id,
                    name: creator.name || creator.handle || 'Unknown Creator',
                    handle: creator.handle || `@${creator.name?.toLowerCase().replace(/\s+/g, '')}` || '@unknown',
                    avatar: creator.profileImage || creator.avatar || '/creatorimages/creatorpic1v2-min.png',
                    profileImage: creator.profileImage || creator.avatar,
                    briefId: briefDoc.id,
                    briefTitle: briefData.title || briefData.name,
                    projectId: normalizedProjectId,
                    completedVideos: completedVideos,
                    approvedAt: submissionData.submittedAt?.toDate ? 
                      submissionData.submittedAt.toDate() : 
                      (submissionData.submittedAt instanceof Date ? 
                        submissionData.submittedAt : 
                        new Date())
                  });
                }
                break; // Found matching creator, move to next submission
              }
            }
          }
        } catch (error) {
          console.error('Error fetching completed creators:', error);
        }
      }

      // Sort by most recently completed (approvedAt) in descending order
      completedCreatorsList.sort((a, b) => {
        const dateA = a.approvedAt || new Date(0);
        const dateB = b.approvedAt || new Date(0);
        return dateB - dateA; // Most recent first
      });

      setCompletedCreators(completedCreatorsList);
    }, (error) => {
      console.error('Error listening to briefs for completed creators:', error);
      setCompletedCreators([]);
    });

    return () => {
      unsubscribeBriefs();
      // Clean up all submission listeners
      for (const unsubscribeFn of submissionUnsubscribes.values()) {
        unsubscribeFn();
      }
      submissionUnsubscribes.clear();
    };
  }, [expandedCampaign]);

  const handleCampaignCreated = (newCampaignName) => {
    const newCampaign = campaigns.find(c => c.name === newCampaignName);
    setSelectedCampaign(newCampaign || null);
    setJustCreatedCampaign(newCampaignName);
    setShowCongratsModal(true);
  };

  const handleEdit = (campaign) => {
    setSelectedCampaign(campaign);
    setShowEditCampaignModal(true);
  };

  const handleDelete = async (campaign) => {
    const activeCreatorsCount = countActiveCreatorsForCampaign(campaign.id);

    if (activeCreatorsCount > 0) {
      alert(`Cannot delete campaign. There ${activeCreatorsCount === 1 ? 'is' : 'are'} ${activeCreatorsCount} active creator${activeCreatorsCount === 1 ? '' : 's'} working on this campaign. Please wait until all creators have completed or been removed before deleting.`);
      return;
    }

    setSelectedCampaign(campaign);
    setShowDeleteConfirm(true);
  };


  const handleCampaignClick = (campaign) => {
    if (expandedCampaign?.id === campaign.id) {
      setExpandedCampaign(null);
    } else {
      setExpandedCampaign(campaign);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!selectedCampaign) return;
    
    // Double-check for active creators before deleting
    const activeCreatorsCount = countActiveCreatorsForCampaign(selectedCampaign.id);

    if (activeCreatorsCount > 0) {
      alert(`Cannot delete campaign. There ${activeCreatorsCount === 1 ? 'is' : 'are'} ${activeCreatorsCount} active creator${activeCreatorsCount === 1 ? '' : 's'} working on this campaign.`);
      setShowDeleteConfirm(false);
      setSelectedCampaign(null);
      return;
    }
    
    setDeleteLoading(true);
    try {
      const auth = getAuth();
      const user = auth.currentUser;
      if (!user) throw new Error('Not authenticated');
      
      const campaignRef = doc(db, 'users', user.uid, 'campaigns', selectedCampaign.id);
      
      // Delete all briefs in the campaign first
      const briefsRef = collection(campaignRef, 'briefs');
      const briefsSnapshot = await getDocs(briefsRef);
      const deletePromises = briefsSnapshot.docs.map(doc => deleteDoc(doc.ref));
      await Promise.all(deletePromises);
      
      // Delete the campaign document
      await deleteDoc(campaignRef);
      
      // Close detail view if the deleted campaign was expanded
      if (expandedCampaign && expandedCampaign.id === selectedCampaign.id) {
        setExpandedCampaign(null);
      }
      
      setShowDeleteConfirm(false);
      setSelectedCampaign(null);
    } catch (err) {
      console.error('Error deleting campaign:', err);
      alert(`Failed to delete campaign: ${err.message}`);
    } finally {
      setDeleteLoading(false);
    }
  };

  const getBrandInitials = (campaignName) => {
    return campaignName
      .split(' ')
      .map(word => word.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const getAvatarColor = (name) => {
    const colors = [
      { bg: '#dbeafe', text: '#1e40af', border: '#bfdbfe' }, // blue
      { bg: '#e9d5ff', text: '#7c3aed', border: '#c4b5fd' }, // purple
      { bg: '#dcfce7', text: '#166534', border: '#bbf7d0' }, // green
      { bg: '#fed7aa', text: '#ea580c', border: '#fdba74' }, // orange
      { bg: '#fce7f3', text: '#be185d', border: '#f9a8d4' }, // pink
      { bg: '#e0e7ff', text: '#3730a3', border: '#c7d2fe' }, // indigo
      { bg: '#ccfbf1', text: '#0f766e', border: '#99f6e4' }, // teal
      { bg: '#ffe4e6', text: '#be123c', border: '#fecaca' }  // rose
    ];
    
    // Generate a consistent color based on the name
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    return colors[Math.abs(hash) % colors.length];
  };


  const formatDate = (dateString) => {
    if (!dateString) return 'No date set';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'active':
        return 'active';
      default:
        return 'campaign';
    }
  };

  const getGoalDisplayName = (goalValue) => {
    const goalMap = {
      'views': 'Increase Views & Reach',
      'engagement': 'Boost Engagement & Interactions',
      'conversions': 'Drive Conversions & Sales',
      'brand_awareness': 'Build Brand Awareness',
      'lead_generation': 'Generate Leads',
      'community': 'Build Community & Following'
    };
    return goalMap[goalValue] || 'Campaign Goal';
  };

  const formatTargetValue = (value) => {
    if (value === null || value === undefined || value === '') return null;
    const number = Number(value);
    if (!Number.isFinite(number) || number <= 0) return null;
    return number.toLocaleString();
  };


  // Brief data for the modal
  const briefsData = [
    {
      title: "Product Launch Brief",
      description: "Comprehensive brief for the main product launch campaign covering key features, target audience, and messaging guidelines.",
      scripts: [
        { name: "Product Launch Script", creatorCount: 3, videoCount: 8 },
        { name: "Feature Highlight Script", creatorCount: 2, videoCount: 5 }
      ],
      creators: []
    },
    {
      title: "Tutorial Content Brief",
      description: "Step-by-step tutorial brief for product demonstration videos focusing on user education and feature highlights.",
      scripts: [
        { name: "Tutorial Script", creatorCount: 2, videoCount: 4 },
        { name: "How-to Script", creatorCount: 1, videoCount: 2 }
      ],
      creators: []
    },
    {
      title: "User Testimonial Brief",
      description: "Authentic user testimonial brief for customer success stories and real-world product experiences.",
      scripts: [
        { name: "Testimonial Script", creatorCount: 1, videoCount: 2 }
      ],
      creators: [
        { handle: "@lifestyle_emma", name: "Lifestyle Emma", avatar: "/creatorimages/creatorpic3v2-min.png", briefs: 1, videos: 1 }
      ]
    }
  ];

  const handleBriefClick = (brief) => {
    setSelectedBrief(brief);
    setShowBriefDetailModal(true);
  };

  const handleViewBrief = () => {
    // Navigate to brief view or open brief editor
    console.log('View brief:', selectedBrief);
    setViewBriefData(selectedBrief);
    setShowViewBriefPanel(true);
    setShowBriefDetailModal(false);
  };

  const handleAddScript = () => {
    // Open the Add Script to Brief modal
    setShowAddScriptToBriefModal(true);
  };

  const handleAddScriptsToBrief = async (scripts) => {
    try {
      const auth = getAuth();
      const user = auth.currentUser;
      if (!user || !selectedBrief) throw new Error('User not authenticated or no brief selected');

      // Update the brief in Firestore to include the new scripts
      const briefRef = doc(db, 'users', user.uid, 'campaigns', selectedBrief.campaignId, 'briefs', selectedBrief.id);
      
      // Get current scripts array and add new ones
      // Brand-added scripts are auto-approved (no approval flow needed)
      const currentScripts = selectedBrief.scripts || [];
      const scriptsWithStatus = scripts.map(s => ({
        ...s,
        approvalStatus: s.approvalStatus || 'approved',
        addedBy: s.addedBy || 'brand'
      }));
      const newScripts = [...currentScripts, ...scriptsWithStatus];
      
      await updateDoc(briefRef, {
        scripts: newScripts,
        updatedAt: new Date().toISOString()
      });
      
      console.log('Scripts added to brief successfully');
      setShowAddScriptToBriefModal(false);
    } catch (error) {
      console.error('Error adding scripts to brief:', error);
      throw error;
    }
  };

  const handleScriptClickFromBrief = (script) => {
    // Open the script in the ClientScriptPanel for viewing/editing
    setViewScriptData(script);
    setShowViewScriptPanel(true);
    // Close the brief modal since we're opening the script panel
    setShowBriefDetailModal(false);
  };

  const handleDeleteBrief = async (brief) => {
    try {
      const auth = getAuth();
      const user = auth.currentUser;
      
      if (!user || !brief.campaignId) {
        throw new Error('User not authenticated or brief missing campaign ID');
      }

      // Delete the brief from Firestore
      const briefRef = doc(db, 'users', user.uid, 'campaigns', brief.campaignId, 'briefs', brief.id);
      await deleteDoc(briefRef);
      
      console.log('Brief deleted successfully:', brief.id);
    } catch (error) {
      console.error('Error deleting brief:', error);
      throw error; // Re-throw so the modal can handle it
    }
  };

  const handleApproveScript = async (script) => {
    try {
      const auth = getAuth();
      const user = auth.currentUser;
      if (!user || !script.campaignId || !script.briefId) throw new Error('User not authenticated or script missing required IDs');

      const briefRef = doc(db, 'users', user.uid, 'campaigns', script.campaignId, 'briefs', script.briefId);
      const briefDoc = await getDoc(briefRef);
      if (!briefDoc.exists()) throw new Error('Brief not found');

      const briefData = briefDoc.data();
      const scripts = briefData.scripts || [];
      const updatedScripts = scripts.map(s =>
        String(s.id) === String(script.id)
          ? { ...s, approvalStatus: 'approved', approvedAt: new Date().toISOString(), approvedBy: user.uid }
          : s
      );
      await updateDoc(briefRef, { scripts: updatedScripts, updatedAt: new Date().toISOString() });
      setShowScriptDetailModal(false);
    } catch (error) {
      console.error('Error approving script:', error);
      throw error;
    }
  };

  const handleRejectScript = async (script) => {
    try {
      const auth = getAuth();
      const user = auth.currentUser;
      if (!user || !script.campaignId || !script.briefId) throw new Error('User not authenticated or script missing required IDs');

      const briefRef = doc(db, 'users', user.uid, 'campaigns', script.campaignId, 'briefs', script.briefId);
      const briefDoc = await getDoc(briefRef);
      if (!briefDoc.exists()) throw new Error('Brief not found');

      const briefData = briefDoc.data();
      const scripts = briefData.scripts || [];
      const updatedScripts = scripts.map(s =>
        String(s.id) === String(script.id)
          ? { ...s, approvalStatus: 'rejected', rejectedAt: new Date().toISOString(), rejectedBy: user.uid }
          : s
      );
      await updateDoc(briefRef, { scripts: updatedScripts, updatedAt: new Date().toISOString() });
      setShowScriptDetailModal(false);
    } catch (error) {
      console.error('Error rejecting script:', error);
      throw error;
    }
  };

  const handleScriptFeedback = async (script, feedback) => {
    try {
      const auth = getAuth();
      const user = auth.currentUser;
      if (!user || !script.campaignId || !script.briefId) throw new Error('User not authenticated or script missing required IDs');

      const briefRef = doc(db, 'users', user.uid, 'campaigns', script.campaignId, 'briefs', script.briefId);
      const briefDoc = await getDoc(briefRef);
      if (!briefDoc.exists()) throw new Error('Brief not found');

      const briefData = briefDoc.data();
      const scripts = briefData.scripts || [];
      const updatedScripts = scripts.map(s =>
        String(s.id) === String(script.id)
          ? { ...s, brandFeedback: feedback, brandFeedbackAt: new Date().toISOString(), brandFeedbackBy: user.uid, approvalStatus: 'feedback_provided' }
          : s
      );
      await updateDoc(briefRef, { scripts: updatedScripts, updatedAt: new Date().toISOString() });
    } catch (error) {
      console.error('Error saving script feedback:', error);
      throw error;
    }
  };

  const handleDeleteScript = async (script) => {
    try {
      const auth = getAuth();
      const user = auth.currentUser;
      
      if (!user || !script.campaignId || !script.briefId) {
        throw new Error('User not authenticated or script missing required IDs');
      }

      console.log('Deleting script:', {
        scriptId: script.id,
        campaignId: script.campaignId,
        briefId: script.briefId,
        scriptName: script.name || script.title
      });

      // Scripts are stored as an array within briefs, not as separate documents
      // Get the brief document
      const briefRef = doc(db, 'users', user.uid, 'campaigns', script.campaignId, 'briefs', script.briefId);
      const briefDoc = await getDoc(briefRef);
      
      if (!briefDoc.exists()) {
        throw new Error('Brief not found');
      }

      const briefData = briefDoc.data();
      const scripts = briefData.scripts || [];
      
      console.log('Brief scripts before deletion:', scripts.map(s => ({ id: s.id, name: s.name || s.title })));
      
      // Remove the script from the array - handle both string and number ID comparisons
      const scriptIdToDelete = String(script.id);
      const updatedScripts = scripts.filter(s => {
        const scriptId = String(s.id);
        return scriptId !== scriptIdToDelete;
      });
      
      console.log('Brief scripts after deletion:', updatedScripts.map(s => ({ id: s.id, name: s.name || s.title })));
      
      if (updatedScripts.length === scripts.length) {
        console.warn('Script not found in brief scripts array. Script ID:', script.id);
        // Still update to ensure consistency, but log the warning
      }
      
      // Update the brief with the new scripts array
      await updateDoc(briefRef, { scripts: updatedScripts });
      
      console.log('Script deleted successfully:', script.id);
    } catch (error) {
      console.error('Error deleting script:', error);
      throw error; // Re-throw so the modal can handle it
    }
  };

  // Script data for the modal
  const scriptsData = [
    {
      title: "Product Launch Script",
      description: "Main script for the product launch campaign focusing on key features and benefits.",
      tag: "Ad",
      creators: [],
      videos: [
        { title: "Feature Walkthrough", creator: "Tech Reviewer Mike", thumbnail: "/creatorimages/Creatorpic2v2-min.png", duration: "3:45", views: "8.9K", likes: "856", date: "Dec 14, 2024" },
        { title: "Quick Overview", creator: "Lifestyle Emma", thumbnail: "/creatorimages/creatorpic3v2-min.png", duration: "1:20", views: "5.2K", likes: "423", date: "Dec 13, 2024" }
      ]
    },
    {
      title: "Tutorial Script",
      description: "Step-by-step tutorial script for product demonstration and usage guide.",
      tag: "Tutorial",
      creators: [],
      videos: [
        { title: "Quick Setup Guide", creator: "Tech Reviewer Mike", thumbnail: "/creatorimages/Creatorpic2v2-min.png", duration: "4:20", views: "9.7K", likes: "1.1K", date: "Dec 11, 2024" }
      ]
    },
    {
      title: "Testimonial Script",
      description: "Customer testimonial and review script for authentic user experiences.",
      tag: "Review",
      creators: [
        { handle: "@lifestyle_emma", name: "Lifestyle Emma", avatar: "/creatorimages/creatorpic3v2-min.png", scripts: 1, videos: 1 }
      ],
      videos: [
        { title: "Customer Success Story", creator: "Lifestyle Emma", thumbnail: "/creatorimages/creatorpic3v2-min.png", duration: "2:10", views: "6.8K", likes: "567", date: "Dec 8, 2024" }
      ]
    }
  ];

  const handleScriptClick = (script) => {
    setSelectedScript(script);
    setShowScriptDetailModal(true);
  };

  const handleViewScript = () => {
    // Open the script in the ClientScriptPanel for viewing/editing
    console.log('View script:', selectedScript);
    setViewScriptData(selectedScript);
    setShowViewScriptPanel(true);
    setShowScriptDetailModal(false);
  };

  const handleAddCreatorToScript = () => {
    // Close script detail modal
    setShowScriptDetailModal(false);
    
    // Find the brief that contains this script
    const scriptBrief = campaignBriefs.find(brief => {
      if (!brief.scripts || !Array.isArray(brief.scripts)) return false;
      return brief.scripts.some(s => String(s.id) === String(selectedScript.id));
    });
    
    // Open AssignCreatorModal with script pre-selected
    setTimeout(() => {
      setActiveTab('findCreators', { 
        preselectScript: selectedScript.id,
        preselectBrief: scriptBrief?.id || null,
        preselectCampaign: selectedScript.campaignId || expandedCampaign?.id || ''
      });
    }, 100);
  };

  const handleAddBriefClick = () => {
    // Open brief creation panel directly - no modal needed
    setShowBriefPanel(true);
  };


  const handleBriefGenerated = async (brief) => {
    // Brief has been generated and auto-saved, update UI immediately
    console.log('Brief generated:', brief);
    console.log('Expanded campaign:', expandedCampaign);
    
    // Add the brief to the local state immediately so it shows up without waiting for Firestore
    setCampaignBriefs(prev => {
      // Check if brief already exists to avoid duplicates
      const exists = prev.some(b => b.id === brief.id && b.campaignId === brief.campaignId);
      if (exists) return prev;
      
      return [...prev, {
        id: brief.id,
        campaignId: brief.campaignId, // Use the campaignId from the brief data
        campaignName: expandedCampaign?.name || '',
        ...brief
      }];
    });
  };

  const handleBriefSaved = async (brief) => {
    setShowBriefPanel(false);
    // Brief has been saved, could refresh data or show success message
    console.log('Brief saved:', brief);
    
    // Add the brief to the local state immediately so it shows up without waiting for Firestore
    setCampaignBriefs(prev => {
      // Check if brief already exists to avoid duplicates
      const exists = prev.some(b => b.id === brief.id && b.campaignId === brief.campaignId);
      if (exists) return prev;
      
      return [...prev, {
        id: brief.id,
        campaignId: brief.campaignId,
        campaignName: expandedCampaign?.name || '',
        ...brief
      }];
    });
    
    // Show small congrats modal prompting to create a script
    setJustCreatedBriefName(brief.name || brief.title || 'Your Brief');
    setShowBriefCreatedModal(true);
  };

  const handleAddScriptClick = () => {
    // Open script creation panel directly - no modal needed
    setShowScriptPanel(true);
  };



  const handleScriptSaved = (scriptData) => {
    setShowScriptPanel(false);
    // Script has been saved, could refresh data or show success message
    console.log('Script saved:', scriptData);
    
    // Extract the script from the data structure
    const script = scriptData.script;
    const campaignId = scriptData.campaign;
    
    // Add the script to the local state immediately so it shows up without waiting for Firestore
    setCampaignScripts(prev => {
      // Check if script already exists to avoid duplicates
      const exists = prev.some(s => s.id === script.id && s.campaignId === campaignId);
      if (exists) return prev;
      
      return [...prev, {
        id: script.id,
        campaignId: campaignId,
        campaignName: expandedCampaign?.name || '',
        ...script
      }];
    });
  };


  const handleRemoveCreatorFromShortlist = async (creator) => {
    if (!expandedCampaign) return;
    
    try {
      const auth = getAuth();
      const user = auth.currentUser;
      if (!user) throw new Error('Not authenticated');

      const campaignRef = doc(db, 'users', user.uid, 'campaigns', expandedCampaign.id);
      const campaignDoc = await getDoc(campaignRef);
      
      if (campaignDoc.exists()) {
        const campaignData = campaignDoc.data();
        const updatedCreators = (campaignData.creators || []).filter(c => c.id !== creator.id);
        
        await updateDoc(campaignRef, {
          creators: updatedCreators
        });
      }
    } catch (error) {
      console.error('Error removing creator from shortlist:', error);
      alert('Failed to remove creator from shortlist');
    }
  };

  // Helper function to calculate script pricing (matches AssignCreatorModal logic)
  const calculateScriptPricing = (script, creatorTier = 'Rising Star') => {
    if (!script || !script.lines) {
      const basePrice = creatorTier === 'Pro' ? 1 : 0.5;
      return { basePrice, extras: 0, total: basePrice };
    }
    
    let extraHooks = 0;
    let extraCTAs = 0;
    
    script.lines.forEach((line, idx) => {
      if (line.type === 'hook' && idx !== 0) extraHooks++;
      if (line.type === 'cta' && idx !== script.lines.length - 1) extraCTAs++;
    });
    
    const basePrice = creatorTier === 'Pro' ? 1 : 0.5; // Pro: 1 credit, Rising Star: 0.5 credits
    const extras = (extraHooks + extraCTAs) * 0.25; // 0.25 credits per extra hook/CTA
    const total = basePrice + extras;
    
    return { basePrice, extras, total, extraHooks, extraCTAs };
  };

  // Confirm a proposed creator (pending_brand_approval -> awaiting_approval, notify creator)
  const handleConfirmProposal = async (creator) => {
    if (!expandedCampaign) return;
    const auth = getAuth();
    const user = auth.currentUser;
    if (!user) return;

    const brief = campaignBriefs.find(b => b.id === creator.briefId && b.campaignId === expandedCampaign.id);
    if (!brief?.scripts) return;

    const scriptIds = creator.scriptIds || [];
    const scripts = brief.scripts.filter(s => scriptIds.includes(s.id) || scriptIds.includes(String(s.id)));
    const creatorTier = creator.tier || 'Rising Star';
    let totalCredits = 0;
    scripts.forEach(script => {
      const { total } = calculateScriptPricing(script, creatorTier);
      totalCredits += total;
    });
    const totalCreditsNeeded = parseFloat(totalCredits.toFixed(2));

    const creditCheck = await CreditService.checkSufficientCredits(totalCreditsNeeded);
    if (!creditCheck.sufficient) {
      setCurrentCredits(creditCheck.currentBalance);
      setShowCreditPurchaseModal(true);
      return;
    }

    try {
      await CreditService.deductCredits(
        totalCreditsNeeded,
        `Confirm proposal: ${creator.name} for ${creator.briefTitle || 'brief'}`,
        { campaignId: expandedCampaign.id, creatorId: creator.id }
      );

      const briefRef = doc(db, 'users', user.uid, 'campaigns', creator.campaignId || expandedCampaign.id, 'briefs', creator.briefId);
      const briefDoc = await getDoc(briefRef);
      if (!briefDoc.exists()) throw new Error('Brief not found');

      const briefData = briefDoc.data();
      const creators = briefData.creators || [];
      const targetProjectId = ProjectIdGenerator.normalizeProjectId(creator.projectId) || creator.projectId;
      // Reset assignedAt and deadline to NOW so creator gets proper accept window (was set at propose time)
      const assignedDate = new Date();
      const defaultDeadline = addBusinessDays(assignedDate, 7);
      const updatedCreators = creators.map(c => {
        const matches = c.id === creator.id && (!targetProjectId || ProjectIdGenerator.areEquivalent(c.projectId || '', targetProjectId));
        if (matches) {
          return {
            ...c,
            status: 'awaiting_approval',
            projectId: targetProjectId,
            assignedAt: assignedDate.toISOString(),
            // Service work starts the 7-day clock immediately. For products, the
            // clock must not run until the creator receives the product, so leave
            // the deadline unset here — it's set on product receipt.
            deadline: (c.productType || '').toLowerCase() === 'product' ? null : defaultDeadline.toISOString(),
          };
        }
        return c;
      });
      await updateDoc(briefRef, {
        creators: updatedCreators,
        creatorIds: arrayUnion(creator.id),
      });

      const campaignDoc = await getDoc(doc(db, 'users', user.uid, 'campaigns', expandedCampaign.id));
      const campaignData = campaignDoc.exists() ? campaignDoc.data() : {};
      const firstScript = scripts[0];
      const creativeControl = firstScript?.settings?.creativeControl || brief?.briefSettings?.creativeControl || 'exact';
      const platform = firstScript?.settings?.platform || campaignData?.platform || 'tiktok';

      // Same as normal assignment flow: notifyCreatorAssignment creates notification + assignment doc
      await NotificationService.notifyCreatorAssignment(creator.id, {
        projectId: targetProjectId,
        campaignId: creator.campaignId || expandedCampaign.id,
        campaignName: expandedCampaign?.name || campaignData?.name,
        briefId: creator.briefId,
        briefTitle: creator.briefTitle || brief?.title || brief?.name || 'Untitled Brief',
        scriptIds: creator.scriptIds || [],
        productType: (creator.productType || 'service').toLowerCase(),
        brandName: campaignData?.brandName || campaignData?.displayName || campaignData?.companyName || campaignData?.agencyName || campaignData?.email || 'Brand',
        creativeControl,
        platform,
      });

      alert(`${creator.name} confirmed. They will receive the assignment notification.`);
    } catch (err) {
      console.error('Error confirming proposal:', err);
      alert(`Failed to confirm: ${err.message}`);
    }
  };

  // Reject a proposed creator (removes from brief, saves feedback for admin, no credits involved)
  const handleRejectProposal = async (feedback = '') => {
    const creator = creatorToRejectProposal;
    if (!expandedCampaign || !creator) return;
    const auth = getAuth();
    const user = auth.currentUser;
    if (!user) return;

    try {
      const briefRef = doc(db, 'users', user.uid, 'campaigns', creator.campaignId || expandedCampaign.id, 'briefs', creator.briefId);
      const briefDoc = await getDoc(briefRef);
      if (!briefDoc.exists()) throw new Error('Brief not found');

      const briefData = briefDoc.data();
      const creators = briefData.creators || [];
      const targetProjectId = ProjectIdGenerator.normalizeProjectId(creator.projectId) || creator.projectId;

      // Remove the creator from the brief (no credits were ever deducted for proposed creators)
      const updatedCreators = creators.filter(c => {
        const matches = c.id === creator.id && (!targetProjectId || ProjectIdGenerator.areEquivalent(c.projectId || '', targetProjectId));
        return !matches;
      });

      // Store rejection feedback for admin review
      const rejectionRecord = {
        creatorId: creator.id,
        creatorName: creator.name || creator.handle || 'Creator',
        creatorHandle: creator.handle || `@${creator.id?.slice(0, 8) || 'creator'}`,
        creatorAvatar: creator.avatar || creator.profileImage || creator.image || null,
        rejectedAt: new Date().toISOString(),
        feedback: feedback || null,
      };
      const existingRejections = briefData.rejectedProposals || [];
      const updatedRejections = [...existingRejections, rejectionRecord];

      await updateDoc(briefRef, {
        creators: updatedCreators,
        creatorIds: arrayRemove(creator.id),
        rejectedProposals: updatedRejections,
      });

      setShowRejectProposalModal(false);
      setCreatorToRejectProposal(null);
      alert(`${creator.name} rejected and removed from the brief.`);
    } catch (err) {
      console.error('Error rejecting proposal:', err);
      alert(`Failed to reject: ${err.message}`);
    }
  };

  // 2 business days (excl. Sat/Sun) from assignment for creator to accept
  const isAcceptExpired = (c) => {
    if (c.status !== 'awaiting_approval' || !c.assignedAt) return false;
    const acceptDeadline = addBusinessDays(new Date(c.assignedAt), ACCEPT_DEADLINE_BUSINESS_DAYS);
    return new Date() > acceptDeadline;
  };

  const isCancelledRefundEligible = (creator) => {
    if (!creator || creator.status !== 'cancelled') return false;

    // Only allow auto-refund for explicit, reviewable cancellation flows.
    // This avoids accidental refunds when a status is stale or unexpectedly flipped.
    if (creator.cancellationRequest?.status === 'approved') return true;

    const cancellationReason = String(creator.cancellationReason || '').toLowerCase();
    if (cancellationReason.includes('auto-cancelled')) return true;

    return false;
  };

  // Handle removing a rejected creator or expired (no-response) assignment; refunds credits.
  // customConfirmMessage: optional; if provided, used instead of the default (rejected) message.
  const handleRemoveRejectedCreator = async (creator, customConfirmMessage) => {
    if (!expandedCampaign) return;

    const rejectedFlow = creator?.status === 'rejected';
    const expiredPendingFlow = creator?.status === 'awaiting_approval' && isAcceptExpired(creator);
    const cancelledRefundFlow = isCancelledRefundEligible(creator);

    if (!rejectedFlow && !expiredPendingFlow && !cancelledRefundFlow) {
      alert('This assignment is not refund-eligible yet. Only rejected, expired pending, or approved cancellation entries can be removed with refund.');
      return;
    }

    const confirmRemoval = window.confirm(
      customConfirmMessage != null
        ? customConfirmMessage
        : `Remove ${creator.name} from this campaign? Their credits will be refunded to your account.`
    );

    if (!confirmRemoval) return;

    try {
      const auth = getAuth();
      const user = auth.currentUser;
      if (!user) throw new Error('Not authenticated');

      // Find the brief this creator is assigned to
      const briefsRef = collection(db, 'users', user.uid, 'campaigns', expandedCampaign.id, 'briefs');
      const briefsSnapshot = await getDocs(briefsRef);

      for (const briefDoc of briefsSnapshot.docs) {
        const briefData = briefDoc.data();
        const creators = briefData.creators || [];
        
        // Find if this creator is in this brief - match by both creatorId AND projectId
        const targetProjectId = creator.projectId ? ProjectIdGenerator.normalizeProjectId(creator.projectId) : null;
        const creatorInBrief = creators.find(c => {
          const matchesCreator = c.id === creator.id;
          if (!matchesCreator) return false;
          
          // If projectId is provided, it MUST match
          if (targetProjectId) {
            const creatorProjectId = c.projectId ? ProjectIdGenerator.normalizeProjectId(c.projectId) : null;
            return creatorProjectId ? ProjectIdGenerator.areEquivalent(creatorProjectId, targetProjectId) : false;
          }
          
          // No projectId provided - only match if creator also has no projectId (backward compatibility)
          const creatorProjectId = c.projectId ? ProjectIdGenerator.normalizeProjectId(c.projectId) : null;
          return !creatorProjectId;
        });
        
        if (creatorInBrief) {
          // Remove ONLY the matching creator entry (creatorId + projectId)
          const normalizedCreatorProjectId = creatorInBrief.projectId ? ProjectIdGenerator.normalizeProjectId(creatorInBrief.projectId) : null;
          const updatedCreators = creators.filter(c => {
            const matchesCreator = c.id === creator.id;
            if (!matchesCreator) return true; // Keep creators with different IDs
            
            // Get creator's projectId
            const cProjectId = c.projectId ? ProjectIdGenerator.normalizeProjectId(c.projectId) : null;
            
            // If target projectId exists, only remove if it matches exactly
            if (normalizedCreatorProjectId) {
              // Remove only if projectId matches
              if (cProjectId && ProjectIdGenerator.areEquivalent(cProjectId, normalizedCreatorProjectId)) {
                return false; // Remove this one
              }
              return true; // Keep all others
            }
            
            // No target projectId - only remove if creator also has no projectId (backward compatibility)
            // This handles old assignments without projectId
            if (!cProjectId) {
              return false; // Remove creator without projectId
            }
            return true; // Keep creators with projectId
          });
          await updateDoc(doc(db, 'users', user.uid, 'campaigns', expandedCampaign.id, 'briefs', briefDoc.id), {
            creators: updatedCreators
          });

          // Calculate credits to refund - recalculate full pricing including extras
          const scriptIds = creatorInBrief.scriptIds || [];
          const scripts = briefData.scripts || [];
          const creatorTier = creator.tier || creatorInBrief.tier || 'Rising Star';
          
          let totalCreditsToRefund = 0;
          
          // Recalculate pricing for each script (matching the original assignment calculation)
          scriptIds.forEach(scriptId => {
            const script = scripts.find(s => s.id === scriptId);
            if (script) {
              const pricing = calculateScriptPricing(script, creatorTier);
              totalCreditsToRefund += pricing.total; // total already includes base + extras
            } else {
              // Fallback: if script not found, use base pricing
              const basePrice = creatorTier === 'Pro' ? 1 : 0.5;
              totalCreditsToRefund += basePrice;
            }
          });
          
          // Round to 2 decimal places to match deduction precision
          const creditsToRefund = parseFloat(totalCreditsToRefund.toFixed(2));

          // Refund credits to user account
          try {
            await CreditService.addCredits(
              creditsToRefund,
              `Refund: Removed ${creator.name} from campaign`,
              {
                campaignId: expandedCampaign.id,
                briefId: briefDoc.id,
                creatorId: creator.id,
                projectId: creator.projectId || creatorInBrief.projectId || null, // Include projectId for tracking
                reason: 'creator_removed'
              }
            );
            console.log(`✅ Refunded ${creditsToRefund} credits for ${creator.name} (${scriptIds.length} script(s), tier: ${creatorTier})`);
            alert(`${creator.name} has been removed. ${creditsToRefund.toFixed(2)} credits have been refunded to your account.`);
          } catch (refundError) {
            console.error('Error refunding credits:', refundError);
            alert(`${creator.name} has been removed, but there was an error refunding credits. Please contact support.`);
          }
          
          break;
        }
      }
    } catch (error) {
      console.error('Error removing rejected creator:', error);
      alert('Failed to remove creator. Please try again.');
    }
  };

  // Handle rejecting a creator (uses centralized rejection + reset logic)
  const handleRejectCreatorForTesting = async (creator) => {
    if (!expandedCampaign) return;

    const confirmRejection = window.confirm(
      `Reject ${creator.name} for testing? This will change their status to 'rejected' so you can test the assignment flow again.`
    );

    if (!confirmRejection) return;

    try {
      const auth = getAuth();
      const user = auth.currentUser;
      if (!user) throw new Error('Not authenticated');

      // Find the brief this creator is assigned to and use centralized service to reject + reset
      const briefsRef = collection(db, 'users', user.uid, 'campaigns', expandedCampaign.id, 'briefs');
      const briefsSnapshot = await getDocs(briefsRef);

      for (const briefDoc of briefsSnapshot.docs) {
        const briefData = briefDoc.data();
        const creators = briefData.creators || [];

        const creatorInBrief = creators.find(c => c.id === creator.id);
        if (!creatorInBrief) continue;

        const assignmentData = {
          campaignId: expandedCampaign.id,
          campaignName: expandedCampaign.name || briefData.campaignName || 'Campaign',
          briefId: briefDoc.id,
          briefTitle: briefData.title || briefData.name || 'Brief',
          creatorName: creator.name || creator.creator || 'Creator'
        };

        await NotificationService.handleCreatorRejection(creator.id, assignmentData);
        alert(`${creator.name} has been rejected and reset. You can now test the assignment flow again.`);
        break;
      }
    } catch (error) {
      console.error('Error rejecting creator for testing:', error);
      alert('Failed to reject creator. Please try again.');
    }
  };

  const handleArchiveCampaign = (campaign) => {
    const activeCreatorsCount = countActiveCreatorsForCampaign(campaign.id);

    if (activeCreatorsCount > 0) {
      alert(`Cannot archive campaign. There ${activeCreatorsCount === 1 ? 'is' : 'are'} ${activeCreatorsCount} active creator${activeCreatorsCount === 1 ? '' : 's'} working on this campaign. Please wait until all creators have completed or been removed before archiving.`);
      return;
    }

    setCampaignToArchive(campaign);
    setShowArchiveConfirm(true);
  };

  const handleArchiveConfirm = async () => {
    if (!campaignToArchive) return;
    
    try {
      const auth = getAuth();
      const user = auth.currentUser;
      if (!user) throw new Error('Not authenticated');
      
      // Double-check for active creators before archiving
      const activeCreatorsCount = countActiveCreatorsForCampaign(campaignToArchive.id);

      if (activeCreatorsCount > 0) {
        alert(`Cannot archive campaign. There ${activeCreatorsCount === 1 ? 'is' : 'are'} ${activeCreatorsCount} active creator${activeCreatorsCount === 1 ? '' : 's'} working on this campaign.`);
        setShowArchiveConfirm(false);
        setCampaignToArchive(null);
        return;
      }
      
      const campaignRef = doc(db, 'users', user.uid, 'campaigns', campaignToArchive.id);
      await updateDoc(campaignRef, {
        archived: true,
        archivedAt: new Date()
      });
      
      setShowArchiveConfirm(false);
      setCampaignToArchive(null);
    } catch (error) {
      console.error('Error archiving campaign:', error);
      alert('Failed to archive campaign. Please try again.');
    }
  };

  const handleUnarchiveCampaign = async (campaign) => {
    try {
      const auth = getAuth();
      const user = auth.currentUser;
      if (!user) throw new Error('Not authenticated');
      
      const campaignRef = doc(db, 'users', user.uid, 'campaigns', campaign.id);
      await updateDoc(campaignRef, {
        archived: false,
        archivedAt: null
      });
    } catch (error) {
      console.error('Error unarchiving campaign:', error);
    }
  };

  const checkScrollButtons = () => {
    if (campaignsGridRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = campaignsGridRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 1);
    }
  };

  const scrollCampaigns = (direction) => {
    if (campaignsGridRef.current) {
      const scrollAmount = 400 + 24; // Width of one card + gap (1.5rem = 24px)
      const newScrollLeft = campaignsGridRef.current.scrollLeft + (direction === 'left' ? -scrollAmount : scrollAmount);
      campaignsGridRef.current.scrollTo({
        left: newScrollLeft,
        behavior: 'smooth'
      });
    }
  };

  const checkScrollButtonsArchived = () => {
    if (archivedCampaignsGridRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = archivedCampaignsGridRef.current;
      setCanScrollLeftArchived(scrollLeft > 0);
      setCanScrollRightArchived(scrollLeft < scrollWidth - clientWidth - 1);
    }
  };

  const scrollArchivedCampaigns = (direction) => {
    if (archivedCampaignsGridRef.current) {
      const scrollAmount = 400 + 24; // Width of one card + gap (1.5rem = 24px)
      const newScrollLeft = archivedCampaignsGridRef.current.scrollLeft + (direction === 'left' ? -scrollAmount : scrollAmount);
      archivedCampaignsGridRef.current.scrollTo({
        left: newScrollLeft,
        behavior: 'smooth'
      });
    }
  };

  // Bottom scroll functions
  const checkBottomScrollButtons = () => {
    const gridRef = campaignFilter === 'active' ? campaignsGridRef : archivedCampaignsGridRef;
    const currentCampaigns = campaignFilter === 'active' 
      ? campaigns.filter(c => !c.archived) 
      : campaigns.filter(c => c.archived);
    
    if (gridRef.current && currentCampaigns.length > 3) {
      const { scrollLeft, scrollWidth, clientWidth } = gridRef.current;
      const hasScrollableContent = scrollWidth > clientWidth;
      
      setCanScrollLeftBottom(scrollLeft > 0 && hasScrollableContent);
      setCanScrollRightBottom(scrollLeft < scrollWidth - clientWidth - 1 && hasScrollableContent);
    } else {
      // Hide arrows when 3 or fewer campaigns
      setCanScrollLeftBottom(false);
      setCanScrollRightBottom(false);
    }
  };

  const scrollCampaignsBottom = (direction) => {
    const gridRef = campaignFilter === 'active' ? campaignsGridRef : archivedCampaignsGridRef;
    if (gridRef.current) {
      const scrollAmount = 400 + 24; // Width of one card + gap (1.5rem = 24px)
      const newScrollLeft = gridRef.current.scrollLeft + (direction === 'left' ? -scrollAmount : scrollAmount);
      gridRef.current.scrollTo({
        left: newScrollLeft,
        behavior: 'smooth'
      });
    }
  };

  // Brief scroll functions
  const checkBriefScrollButtons = () => {
    const currentBriefs = campaignBriefs.filter(b => b.campaignId === expandedCampaign?.id);
    const isMobile = window.innerWidth < 768;
    const maxVisibleCards = isMobile ? 3 : 4;
    
    console.log('Brief scroll check:', {
      currentBriefs: currentBriefs.length,
      maxVisibleCards,
      expandedCampaign: expandedCampaign?.id,
      hasGridRef: !!briefsGridRef.current
    });
    
    if (briefsGridRef.current && currentBriefs.length > maxVisibleCards) {
      const { scrollLeft, scrollWidth, clientWidth } = briefsGridRef.current;
      const hasScrollableContent = scrollWidth > clientWidth;
      
      console.log('Scroll metrics:', { scrollLeft, scrollWidth, clientWidth, hasScrollableContent });
      
      setCanScrollLeftBriefs(scrollLeft > 0 && hasScrollableContent);
      setCanScrollRightBriefs(scrollLeft < scrollWidth - clientWidth - 1 && hasScrollableContent);
    } else {
      // Hide arrows when maxVisibleCards or fewer briefs
      setCanScrollLeftBriefs(false);
      setCanScrollRightBriefs(false);
    }
  };

  const scrollBriefs = (direction) => {
    if (briefsGridRef.current) {
      const isMobile = window.innerWidth < 768;
      const cardWidth = isMobile ? 260 : 280;
      const scrollAmount = cardWidth + 16; // Width of one brief card + gap (1rem = 16px)
      const newScrollLeft = briefsGridRef.current.scrollLeft + (direction === 'left' ? -scrollAmount : scrollAmount);
      briefsGridRef.current.scrollTo({
        left: newScrollLeft,
        behavior: 'smooth'
      });
    }
  };

  // Script scroll functions
  const checkScriptScrollButtons = () => {
    const currentScripts = campaignScripts.filter(s => s.campaignId === expandedCampaign?.id);
    const isMobile = window.innerWidth < 768;
    const maxVisibleCards = isMobile ? 3 : 4;
    
    console.log('Script scroll check:', {
      currentScripts: currentScripts.length,
      maxVisibleCards,
      expandedCampaign: expandedCampaign?.id,
      hasGridRef: !!scriptsGridRef.current
    });
    
    if (scriptsGridRef.current && currentScripts.length > maxVisibleCards) {
      const { scrollLeft, scrollWidth, clientWidth } = scriptsGridRef.current;
      const hasScrollableContent = scrollWidth > clientWidth;
      
      console.log('Script scroll metrics:', { scrollLeft, scrollWidth, clientWidth, hasScrollableContent });
      
      setCanScrollLeftScripts(scrollLeft > 0 && hasScrollableContent);
      setCanScrollRightScripts(scrollLeft < scrollWidth - clientWidth - 1 && hasScrollableContent);
    } else {
      setCanScrollLeftScripts(false);
      setCanScrollRightScripts(false);
    }
  };

  const scrollScripts = (direction) => {
    if (scriptsGridRef.current) {
      const isMobile = window.innerWidth < 768;
      const cardWidth = isMobile ? 260 : 280;
      const scrollAmount = cardWidth + 16; // Width of one script card + gap (1rem = 16px)
      const newScrollLeft = scriptsGridRef.current.scrollLeft + (direction === 'left' ? -scrollAmount : scrollAmount);
      scriptsGridRef.current.scrollTo({
        left: newScrollLeft,
        behavior: 'smooth'
      });
    }
  };

  // Creators scroll functions
  const checkCreatorsScrollButtons = () => {
    if (!creatorsGridRef.current || !expandedCampaign) return;

    const workingFiltered = currentlyWorkingCreators.filter(c => c.status !== 'pending_brand_approval');
    const filteredCreators = workingFiltered.filter(creator => {
      if (creatorStatusFilter === 'all') return true;
      if (creatorStatusFilter === 'accepted') {
        return creator.status &&
               creator.status !== 'awaiting_approval' &&
               creator.status !== 'rejected' &&
               creator.status !== 'cancelled' &&
               creator.status !== 'completed';
      }
      if (creatorStatusFilter === 'awaiting_approval') {
        return creator.status === 'awaiting_approval';
      }
      return true;
    });

    const { scrollLeft, scrollWidth, clientWidth } = creatorsGridRef.current;
    const hasScrollableContent = scrollWidth > clientWidth + 2;

    // Arrows appear once there is a 4th creator (viewport fits up to 4).
    if (filteredCreators.length <= 3 || !hasScrollableContent) {
      setCanScrollLeftCreators(false);
      setCanScrollRightCreators(false);
      return;
    }

    setCanScrollLeftCreators(scrollLeft > 2);
    setCanScrollRightCreators(scrollLeft < scrollWidth - clientWidth - 2);
  };

  const scrollCreators = (direction) => {
    if (creatorsGridRef.current) {
      const isMobile = window.innerWidth < 768;
      const cardWidth = isMobile ? 260 : 280;
      const scrollAmount = cardWidth + 16; // Width of one creator card + gap (1rem = 16px)
      const newScrollLeft = creatorsGridRef.current.scrollLeft + (direction === 'left' ? -scrollAmount : scrollAmount);
      creatorsGridRef.current.scrollTo({
        left: newScrollLeft,
        behavior: 'smooth'
      });
    }
  };

  // Scroll to section functions
  const scrollToSection = (sectionRef) => {
    if (sectionRef.current) {
      sectionRef.current.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'start',
        inline: 'nearest'
      });
    }
  };

  const scrollToBriefs = () => scrollToSection(briefsSectionRef);
  const scrollToScripts = () => scrollToSection(scriptsSectionRef);
  const scrollToCreators = () => scrollToSection(creatorsSectionRef);

  const hasPendingForCampaign = expandedCampaign && campaignScripts.some(
    s => {
      if (s.campaignId !== expandedCampaign.id || s.isDraft) return false;
      if (s.approvalStatus === 'approved' || s.approvalStatus === 'rejected') return false;
      return s.approvalStatus === 'pending_brand_approval' || s.addedBy === 'admin';
    }
  );

  // Admin-added scripts pending brand approval - dedicated section so brand can View, Confirm, or Give feedback
  // Exclude feedback_provided (script removed from pending until admin makes changes)
  const adminPendingScripts = expandedCampaign
    ? campaignScripts.filter(
        s => {
          if (s.campaignId !== expandedCampaign.id || s.isDraft) return false;
          if (s.approvalStatus === 'approved' || s.approvalStatus === 'rejected' || s.approvalStatus === 'feedback_provided') return false;
          return s.approvalStatus === 'pending_brand_approval' || s.addedBy === 'admin';
        }
      )
    : [];

  // Helper function to get filtered scripts
  const getFilteredScripts = () => {
    if (!expandedCampaign) return [];
    return campaignScripts
      .filter(s => s.campaignId === expandedCampaign.id)
      .filter(s => (scriptBriefFilter === 'all' ? true : s.briefId === scriptBriefFilter))
      .filter(s => {
        if (scriptStatusFilter === 'drafts') return s.isDraft;
        if (scriptStatusFilter === 'pending') {
          if (s.isDraft) return false;
          if (s.approvalStatus === 'approved' || s.approvalStatus === 'rejected') return false;
          return s.approvalStatus === 'pending_brand_approval' || s.addedBy === 'admin';
        }
        // complete = non-draft, approved (or legacy); exclude admin-pending so they only show in "Scripts from your account manager"
        const isAdminPending =
          (s.approvalStatus === 'pending_brand_approval' || s.addedBy === 'admin') &&
          s.approvalStatus !== 'approved' &&
          s.approvalStatus !== 'rejected';
        return !s.isDraft && !isAdminPending;
      });
  };

  // Helper function to calculate script cost
  const calculateScriptCost = (script) => {
    const settings = script.settings || {};
    const additionalHooks = settings.additionalHooks || 0;
    const additionalCTAs = settings.additionalCTAs || 0;
    
    // Base cost is 1 credit, additional hooks/CTAs are 0.25 credits each
    const baseCost = 1;
    const additionalCost = (additionalHooks + additionalCTAs) * 0.25;
    const totalCost = baseCost + additionalCost;

    const creativeControl = (() => {
      switch ((settings.creativeControl || 'exact')) {
        case 'exact':
          return 'scripted';
        case 'some':
          return 'creative';
        case 'full':
          return 'creative';
        default:
          return 'scripted';
      }
    })();
    
    return {
      baseCost,
      additionalCost,
      totalCost,
      additionalHooks,
      additionalCTAs,
      creativeControl
    };
  };

  const briefsForExpandedCampaign = expandedCampaign
    ? campaignBriefs.filter(b => b.campaignId === expandedCampaign.id)
    : [];

  // Creators proposed by admin (pending_brand_approval) - shown in their own section above Currently Working
  const proposedCreators = useMemo(() => {
    if (!expandedCampaign) return [];
    const list = [];
    briefsForExpandedCampaign.forEach((brief) => {
      (brief.creators || []).forEach((creator) => {
        if (creator.status === 'pending_brand_approval') {
          const rawProjectId = creator.projectId || creator.progress?.projectId || null;
          const normalizedProjectId = ProjectIdGenerator.normalizeProjectId(rawProjectId) || rawProjectId || null;
          list.push({
            ...creator,
            projectId: normalizedProjectId,
            briefId: brief.id,
            briefTitle: brief.title || brief.name,
            campaignId: brief.campaignId || expandedCampaign?.id,
          });
        }
      });
    });
    return list;
  }, [expandedCampaign, briefsForExpandedCampaign]);

  const hasDraftScriptsForExpandedCampaign = expandedCampaign
    ? campaignScripts.some(s => s.campaignId === expandedCampaign.id && s.isDraft)
    : false;

  if (isLoading) {
  return (
      <div className="campaigns-container">
        <div className="campaigns-overview-container">
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            <div style={{ fontSize: '1.125rem', color: '#64748b' }}>Loading campaigns...</div>
          </div>
        </div>
      </div>
    );
  }

  const formattedGoalViews = formatTargetValue(expandedCampaign?.goalViews);
  const formattedGoalConversions = formatTargetValue(expandedCampaign?.goalConversions);
  const hasCampaignTargets = Boolean(formattedGoalViews || formattedGoalConversions);

  return (
    <div className="campaigns-container brand-campaigns-spacing">
        {/* Header Section */}
        <div className="campaigns-overview-container">
          <div className="campaigns-overview-header">
            <div className="campaigns-header-content">
              <div className="campaigns-header-text">
                <h1 className="campaigns-overview-title">Campaigns</h1>
                <p className="campaigns-overview-subtitle">Manage your marketing campaigns and track their performance</p>
              </div>
              <button 
                className="new-campaign-btn"
                onClick={() => setShowCreateCampaignModal(true)}
              >
                <Plus size={16} />
                Create New Campaign
          </button>
      </div>
          </div>

        {/* Campaign Search */}
        <div className="campaigns-sort-section">
          <div className="campaigns-sort-container">
            <label className="campaigns-sort-label">Sort by:</label>
            <div className="campaigns-sort-dropdown">
              <select
                value={campaignSortBy}
                onChange={(e) => setCampaignSortBy(e.target.value)}
                className="campaigns-sort-select"
              >
                <option value="recent">Most Recent</option>
                <option value="alphabetical">Alphabetical</option>
                <option value="active">Most Active</option>
              </select>
              <ChevronDown size={16} className="campaigns-sort-icon" />
      </div>
          </div>
        </div>

        {/* Campaign Filter Tabs */}
        <div className="campaigns-filter-tabs">
          <button 
            className={`campaign-filter-tab ${campaignFilter === 'active' ? 'active' : ''}`}
            onClick={() => setCampaignFilter('active')}
          >
            Active Campaigns
            <span className="filter-tab-count">
              {campaigns.filter(c => !c.archived).length}
            </span>
          </button>
          <button 
            className={`campaign-filter-tab ${campaignFilter === 'archived' ? 'active' : ''}`}
            onClick={() => setCampaignFilter('archived')}
          >
            Archived Campaigns
            <span className="filter-tab-count">
              {campaigns.filter(c => c.archived).length}
            </span>
          </button>
      </div>

        {/* Campaigns Grid */}
        {(() => {
          const filteredCampaigns = campaigns.filter(campaign => {
            // Filter by active/archived status
            const matchesStatus = campaignFilter === 'active' ? !campaign.archived : campaign.archived;
            return matchesStatus;
          });

          // Sort campaigns based on selected option
          const sortedCampaigns = [...filteredCampaigns].sort((a, b) => {
            switch (campaignSortBy) {
              case 'recent':
                // Most recently created (newest first)
                const aDate = new Date(a.createdAt || a.created || 0);
                const bDate = new Date(b.createdAt || b.created || 0);
                return bDate - aDate;
              
              case 'alphabetical':
                // Alphabetical by name
                return (a.name || '').localeCompare(b.name || '');
              
              case 'active':
                // Most active (most briefs + scripts)
                const aActivity = (a.briefsCount || 0) + (a.scriptsCount || 0);
                const bActivity = (b.briefsCount || 0) + (b.scriptsCount || 0);
                return bActivity - aActivity;
              
              default:
                return 0;
            }
          });
          
          return sortedCampaigns.length === 0 ? (
            <div className="campaigns-empty-state">
              <div className="campaigns-empty-state-icon">
                {campaignFilter === 'active' ? '📊' : '📦'}
            </div>
              <h3>
                {campaignFilter === 'active' ? 'No active campaigns' : 'No archived campaigns'}
              </h3>
              <p>
                {campaignFilter === 'active' 
                  ? 'Create your first campaign to start managing your marketing efforts and tracking performance.'
                  : 'Archived campaigns will appear here when you archive them from the active campaigns.'
                }
              </p>
            </div>
          ) : (
            <div className="campaigns-grid-container">
              {(campaignFilter === 'active' ? canScrollLeft : canScrollLeftArchived) && (
                <button 
                  className="campaigns-scroll-btn campaigns-scroll-left"
                  onClick={() => campaignFilter === 'active' ? scrollCampaigns('left') : scrollArchivedCampaigns('left')}
                >
                  <ChevronLeft size={20} />
                </button>
              )}
              <div 
                className="campaigns-grid"
                ref={campaignFilter === 'active' ? campaignsGridRef : archivedCampaignsGridRef}
                onScroll={() => {
                  if (campaignFilter === 'active') {
                    checkScrollButtons();
                  } else {
                    checkScrollButtonsArchived();
                  }
                  checkBottomScrollButtons();
                }}
              >
                {sortedCampaigns.map((campaign) => (
              <div key={campaign.id} className={`campaign-card ${campaign.archived ? 'archived' : ''}`} onClick={() => handleCampaignClick(campaign)}>
                {/* Card Header */}
                <div className="campaign-card-header">
                  <div className="campaign-card-brand">
                    <div 
                      className="campaign-card-brand-avatar"
                  style={{ 
                        background: campaign.avatarColor?.includes('gradient') ? campaign.avatarColor : undefined,
                        backgroundColor: campaign.avatarColor?.includes('gradient') ? undefined : (campaign.avatarColor || getAvatarColor(campaign.name).bg),
                        color: campaign.avatarColor ? 'white' : getAvatarColor(campaign.name).text,
                        borderColor: campaign.avatarColor ? 'transparent' : getAvatarColor(campaign.name).border
                      }}
                    >
                      {campaign.avatarImage ? (
                        <img 
                          src={campaign.avatarImage} 
                          alt={`${campaign.name} avatar`}
                          style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 'inherit' }}
                        />
                      ) : (
                        getBrandInitials(campaign.name)
                      )}
                </div>
                          <div className="campaign-card-brand-info">
                            <div className="campaign-card-brand-name">{campaign.name}</div>
                            <div className="campaign-card-campaign-name">{getGoalDisplayName(campaign.primaryGoal)}</div>
                          </div>
                  </div>
                </div>

                {/* Card Content */}
                <div className="campaign-card-content">
                  {/* Stats */}
                  <div className="campaign-card-stats">
                    <div className="campaign-card-stat">
                      <span className="campaign-card-stat-value">{campaignBriefs.filter(b => b.campaignId === campaign.id).length}</span>
                      <span className="campaign-card-stat-label">Briefs</span>
                    </div>
                    <div className="campaign-card-stat">
                      <span className="campaign-card-stat-value">{campaignScripts.filter(s => s.campaignId === campaign.id).length}</span>
                      <span className="campaign-card-stat-label">Scripts</span>
                    </div>
                          <div className="campaign-card-stat">
                            <span className="campaign-card-stat-value">
                              {countActiveCreatorsForCampaign(campaign.id)}
                            </span>
                            <span className="campaign-card-stat-label">Active</span>
                          </div>
                  </div>

                </div>

                {/* Card Footer */}
                <div className="campaign-card-footer">
                  <div className="campaign-card-actions">
                    <button 
                      className="campaign-card-action-btn-icon"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEdit(campaign);
                      }}
                      onMouseEnter={(e) => {
                        const buttonRect = e.currentTarget.getBoundingClientRect();
                        setTooltip({
                          visible: true,
                          text: 'Edit Campaign',
                          position: {
                            top: buttonRect.top - 40,
                            left: buttonRect.left + buttonRect.width / 2
                          }
                        });
                      }}
                      onMouseLeave={() => {
                        setTooltip({ visible: false, text: '', position: { top: 0, left: 0 } });
                      }}
                    >
                      <FileText size={16} />
                    </button>
                    <button 
                      className="campaign-card-action-btn-icon"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (!campaign.archived) {
                          handleArchiveCampaign(campaign);
                    } else {
                          handleUnarchiveCampaign(campaign);
                        }
                      }}
                      onMouseEnter={(e) => {
                        const buttonRect = e.currentTarget.getBoundingClientRect();
                        setTooltip({
                          visible: true,
                          text: !campaign.archived ? "Archive Campaign" : "Unarchive Campaign",
                          position: {
                            top: buttonRect.top - 40,
                            left: buttonRect.left + buttonRect.width / 2
                          }
                        });
                      }}
                      onMouseLeave={() => {
                        setTooltip({ visible: false, text: '', position: { top: 0, left: 0 } });
                      }}
                    >
                      <Archive size={16} />
                    </button>
                    <button 
                      className="campaign-card-action-btn-icon campaign-card-delete-btn-icon"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(campaign);
                      }}
                      onMouseEnter={(e) => {
                        const buttonRect = e.currentTarget.getBoundingClientRect();
                        setTooltip({
                          visible: true,
                          text: 'Delete Campaign',
                          position: {
                            top: buttonRect.top - 40,
                            left: buttonRect.left + buttonRect.width / 2
                          }
                        });
                      }}
                      onMouseLeave={() => {
                        setTooltip({ visible: false, text: '', position: { top: 0, left: 0 } });
                      }}
                    >
                      <Trash2 size={16} />
                    </button>
                </div>
            </div>

                        </div>
            ))}
                </div>
              {(campaignFilter === 'active' ? canScrollRight : canScrollRightArchived) && (
                <button 
                  className="campaigns-scroll-btn campaigns-scroll-right"
                  onClick={() => campaignFilter === 'active' ? scrollCampaigns('right') : scrollArchivedCampaigns('right')}
                >
                  <ChevronRight size={20} />
                </button>
              )}
            </div>
          );
        })()}
        
        {/* Bottom Scroll Controls */}
        <div className="campaigns-bottom-scroll-controls">
          {canScrollLeftBottom && (
            <button 
              className="campaigns-bottom-scroll-btn"
              onClick={() => scrollCampaignsBottom('left')}
            >
              <ChevronLeft size={20} />
            </button>
          )}
          {canScrollRightBottom && (
            <button 
              className="campaigns-bottom-scroll-btn"
              onClick={() => scrollCampaignsBottom('right')}
            >
              <ChevronRight size={20} />
            </button>
          )}
        </div>

        {/* Campaign Detail Block - Separate from cards */}
        {expandedCampaign && (
          <div className="campaign-detail-block">
            <div className="detail-block-header">
              <div className="detail-block-title-section">
                <h3 className="detail-block-title">{expandedCampaign.name}</h3>
                <p className="detail-block-subtitle">{getGoalDisplayName(expandedCampaign.primaryGoal)}</p>
                {hasCampaignTargets && (
                  <div className="detail-inline-goals">
                    {formattedGoalViews && (
                      <span className="detail-inline-goal-chip">
                        Views · {formattedGoalViews}
                      </span>
                    )}
                    {formattedGoalConversions && (
                      <span className="detail-inline-goal-chip">
                        Conversions · {formattedGoalConversions}
                      </span>
                    )}
                  </div>
                )}
          </div>
        </div>

            <div className="detail-block-content">
              {/* Stats Grid */}
              <div className="detail-stats-grid">
                <div className="detail-stat-card" onClick={scrollToBriefs} style={{ cursor: 'pointer' }}>
                  <div className="detail-stat-header">
                    <div className="detail-stat-icon briefs">
                      <FileText size={24} />
                    </div>
                    <div className="detail-stat-content">
                      <div className="detail-stat-label">Briefs</div>
                    </div>
                    <div className="detail-stat-number">{campaignBriefs.filter(b => b.campaignId === expandedCampaign.id).length}</div>
                  </div>
                </div>

                <div className="detail-stat-card" onClick={scrollToScripts} style={{ cursor: 'pointer' }}>
                  <div className="detail-stat-header">
                    <div className="detail-stat-icon scripts">
                      <Play size={24} />
                    </div>
                    <div className="detail-stat-content">
                      <div className="detail-stat-label">Scripts</div>
                    </div>
                    <div className="detail-stat-number">{campaignScripts.filter(s => s.campaignId === expandedCampaign.id).length}</div>
                  </div>
                </div>

                <div className="detail-stat-card" onClick={scrollToCreators} style={{ cursor: 'pointer' }}>
                  <div className="detail-stat-header">
                    <div className="detail-stat-icon creators">
                      <Users size={24} />
                    </div>
                    <div className="detail-stat-content">
                      <div className="detail-stat-label">Active Creators</div>
                    </div>
                    <div className="detail-stat-number">
                      {countActiveCreatorsForCampaign(expandedCampaign.id)}
                    </div>
                  </div>
                </div>

              </div>

              {/* Campaign Description Section */}
              {expandedCampaign.description && (
                <div className="detail-description-section">
                  <div className="detail-description-header">
                    <h4 className="detail-description-title">Campaign Description</h4>
                  </div>
                  <div className="detail-description-content">
                    <p className="detail-description-text">{expandedCampaign.description}</p>
                  </div>
                          </div>
                        )}

              {/* Content Sections */}
              <div className="detail-sections">
                {/* Briefs Section */}
                <div className="detail-section" ref={briefsSectionRef}>
                  <div className="detail-section-header">
                    <div className="detail-section-title-group scripts-title-group">
                      <h4 className="detail-section-title">Briefs</h4>
                    </div>
                    <button 
                      className="add-brief-button"
                      onClick={handleAddBriefClick}
                    >
                      <Plus size={16} />
                      Add Brief
                    </button>
                  </div>

                  <div className="briefs-cards-grid" ref={briefsGridRef} onScroll={checkBriefScrollButtons}>
                    {briefsLoading ? (
                      <div className="loading-state">
                        <div className="loading-spinner"></div>
                        <p>Loading briefs...</p>
                      </div>
                    ) : campaignBriefs.filter(b => b.campaignId === expandedCampaign.id).length === 0 ? (
                      <div className="empty-state-prominent">
                        <div className="empty-state-content">
                          <div className="empty-state-icon-large">
                            <FileText size={64} />
                          </div>
                          <h3>Create Your First Brief</h3>
                          <p>Start building your campaign by creating a brief that outlines your content requirements</p>
                          <button 
                            className="empty-state-primary-btn"
                            onClick={handleAddBriefClick}
                          >
                            <Plus size={20} />
                            Create Brief
                          </button>
                        </div>
                      </div>
                    ) : (
                      campaignBriefs
                        .filter(b => b.campaignId === expandedCampaign.id)
                        .sort((a, b) => {
                          const dateA = new Date(a.createdAt || a.created || 0);
                          const dateB = new Date(b.createdAt || b.created || 0);
                          return dateB - dateA; // Newest first
                        })
                        .map((brief, index) => {
                          console.log(`=== RENDERING BRIEF CARD ===`);
                          console.log(`Brief:`, brief);
                          console.log(`Brief name:`, brief.name);
                          console.log(`Brief title:`, brief.title);
                          console.log(`Brief briefDescription:`, brief.briefDescription);
                          console.log(`Brief overview:`, brief.overview);
                          console.log(`Brief description:`, brief.description);
                          console.log(`Brief content:`, brief.content);
                          console.log(`Brief summary:`, brief.summary);
                          console.log(`Brief aiResponse:`, brief.aiResponse);
                          console.log(`Brief aiResponse length:`, brief.aiResponse ? brief.aiResponse.length : 'No aiResponse');
                          console.log(`All brief fields:`, Object.keys(brief));
                          console.log(`Brief values:`, Object.values(brief));
                          return (
                          <div key={brief.id} className="modern-brief-card" onClick={() => handleBriefClick(brief)}>
                            {/* Image Section */}
                            <div className="modern-brief-card-image">
                              {brief.productImage ? (
                                <img src={brief.productImage} alt="Product" className="brief-product-image" />
                              ) : expandedCampaign?.avatar || expandedCampaign?.profileImage ? (
                                <img 
                                  src={expandedCampaign.avatar || expandedCampaign.profileImage} 
                                  alt="Campaign" 
                                  className="brief-campaign-image" 
                                />
                              ) : (
                                <div className="brief-placeholder-image">
                                  <div className="brief-placeholder-content">
                                    <FileText size={32} />
                                    <span>UGC Brief</span>
                                  </div>
                                </div>
                              )}
                              
                              {/* New Tag - Show if within 2 days */}
                              {(() => {
                                const date = brief.createdAt || brief.created || new Date().toISOString();
                                const createdDate = new Date(date);
                                const now = new Date();
                                const diffTime = Math.abs(now - createdDate);
                                const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                                return diffDays <= 2 ? (
                                  <div className="brief-new-tag">
                                    New
                                  </div>
                                ) : null;
                              })()}
                            </div>
                            
                            {/* Content Section */}
                            <div className="modern-brief-card-content">
                              <div className="modern-brief-card-text">
                                <h5 className="modern-brief-card-title">
                                  {brief.name || brief.title || brief.briefName || brief.briefTitle || 'Untitled Brief'}
                                </h5>
                                <p className="modern-brief-card-description">
                                  {(() => {
                                    let description = '';
                                    
                                    if (brief.aiResponse) {
                                      const sections = {};
                                      const lines = brief.aiResponse.split('\n');
                                      let currentSection = '';
                                      let currentContent = [];
                                      
                                      lines.forEach(line => {
                                        if (line.startsWith('## ')) {
                                          if (currentSection) {
                                            sections[currentSection] = currentContent.join('\n').trim();
                                          }
                                          currentSection = line.replace('## ', '').trim();
                                          currentContent = [];
                                        } else {
                                          currentContent.push(line);
                                        }
                                      });
                                      
                                      if (currentSection) {
                                        sections[currentSection] = currentContent.join('\n').trim();
                                      }
                                      
                                      description = sections['QUICK BRIEF DESCRIPTION'] || sections['1. BRIEF OVERVIEW'] || sections['BRIEF OVERVIEW'] || sections['briefOverview'] || brief.manualBriefSections?.quickBriefDescription || brief.briefOverview || brief.overview || brief.content || brief.summary || brief.description || 'No overview available';
                                    } else {
                                      description = brief.manualBriefSections?.quickBriefDescription || brief.briefOverview || brief.overview || brief.content || brief.summary || brief.description || 'No overview available';
                                    }
                                    
                                    // Truncate description to fit card layout (max 100 characters)
                                    console.log('Brief description before truncation:', description.length, description);
                                    if (description.length > 100) {
                                      description = description.substring(0, 100).trim() + '...';
                                      console.log('Brief description after truncation:', description);
                                    }
                                    
                                    return description;
                                  })()}
                                </p>
                              </div>
                              
                              {/* Tags Section */}
                              <div className="modern-brief-card-tags">
                                <div className="modern-brief-tag script-tag">
                                  <FileText size={12} />
                                  <span>{brief.scripts ? brief.scripts.length : 0} scripts</span>
                                </div>
                                <div className="modern-brief-tag creator-tag">
                                  <Users size={12} />
                                  <span>
                                    {brief.creators 
                                      ? brief.creators.filter(creator => 
                                          creator.status && 
                                          creator.status !== 'awaiting_approval' &&
                                          creator.status !== 'rejected' && 
                                          creator.status !== 'completed'
                                        ).length 
                                      : 0} creators
                                  </span>
                                </div>
                              </div>
                              
                              {/* Date Created */}
                              <div className="modern-brief-card-date">
                                {(() => {
                                  const date = brief.createdAt || brief.created || new Date().toISOString();
                                  const createdDate = new Date(date);
                                  return createdDate.toLocaleDateString('en-US', { 
                                    month: 'short', 
                                    day: 'numeric',
                                    year: 'numeric'
                                  });
                                })()}
                              </div>
                            </div>
                          </div>
                          );
                        })
                    )}
                  </div>
                  
                  {/* Brief Scroll Controls - Only show when campaign is expanded */}
                  {expandedCampaign && (
                    <div className="briefs-scroll-controls">
                      {console.log('Rendering brief scroll controls:', { canScrollLeftBriefs, canScrollRightBriefs })}
                      {(() => {
                        // Check scroll buttons when rendering
                        setTimeout(() => checkBriefScrollButtons(), 100);
                        return null;
                      })()}
                      {canScrollLeftBriefs && (
                        <button 
                          className="briefs-scroll-btn"
                          onClick={() => scrollBriefs('left')}
                        >
                          <ChevronLeft size={20} />
                        </button>
                      )}
                      {canScrollRightBriefs && (
                        <button 
                          className="briefs-scroll-btn"
                          onClick={() => scrollBriefs('right')}
                        >
                          <ChevronRight size={20} />
                        </button>
                      )}
                    </div>
                  )}
                </div>
            </div>

                {/* Section Separator */}
                <div className="section-separator"></div>

                {/* Scripts Section */}
                <div className="detail-section" ref={scriptsSectionRef}>
                  <div className="detail-section-header scripts-header">
                    <div className="detail-section-title-group">
                      <h4 className="detail-section-title">Scripts</h4>
                    </div>
                        <button 
                      className={`add-script-button ${briefsForExpandedCampaign.length === 0 ? 'disabled' : ''}`}
                      onClick={briefsForExpandedCampaign.length === 0 ? undefined : handleAddScriptClick}
                      disabled={briefsForExpandedCampaign.length === 0}
                        >
                      <Plus size={16} />
                      Add Script
                        </button>
                  </div>

                  {/* Admin-added scripts – separate section when managed account has scripts pending approval */}
                  {adminPendingScripts.length > 0 && (
                    <div className="admin-pending-scripts-section">
                      <div className="admin-pending-scripts-header">
                        <h4 className="admin-pending-scripts-title">Scripts from your account manager</h4>
                        <p className="admin-pending-scripts-desc">These scripts were added for you. View (and edit if needed), confirm to use them, or send feedback.</p>
                      </div>
                      <div className="admin-pending-scripts-grid">
                        {adminPendingScripts.map((script) => (
                          <div key={script.id} className="admin-pending-script-card">
                            <div className="admin-pending-script-info">
                              <h5 className="admin-pending-script-name">{script.name || script.title || `Script ${script.id}`}</h5>
                              <p className="admin-pending-script-brief">
                                Brief: {briefsForExpandedCampaign.find(b => b.id === script.briefId)?.title || briefsForExpandedCampaign.find(b => b.id === script.briefId)?.name || '—'}
                              </p>
                            </div>
                            <div className="admin-pending-script-actions">
                              <button
                                type="button"
                                className="admin-pending-script-btn view"
                                onClick={() => {
                                  setViewScriptData(script);
                                  setShowViewScriptPanel(true);
                                  setShowScriptDetailModal(false);
                                }}
                              >
                                <FileText size={14} />
                                View
                              </button>
                              <button
                                type="button"
                                className="admin-pending-script-btn confirm"
                                onClick={() => handleApproveScript(script)}
                              >
                                <CheckCircle size={14} />
                                Confirm
                              </button>
                              <button
                                type="button"
                                className="admin-pending-script-btn feedback"
                                onClick={() => {
                                  setSelectedScript(script);
                                  setShowScriptFeedbackModal(true);
                                }}
                              >
                                <MessageSquare size={14} />
                                Give feedback
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="dashboard-scripts-filters">
                    <div className="dashboard-scripts-brief-filter">
                      <label htmlFor="dashboard-scripts-brief-filter-select">Filter by brief</label>
                      <select
                        id="dashboard-scripts-brief-filter-select"
                        value={scriptBriefFilter}
                        onChange={(event) => setScriptBriefFilter(event.target.value)}
                      >
                        <option value="all">All briefs</option>
                        {briefsForExpandedCampaign.map((brief) => (
                          <option key={brief.id} value={brief.id}>
                            {brief.title || brief.name || 'Untitled Brief'}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="dashboard-scripts-status-filter">
                      <span>Status</span>
                      <div className="dashboard-scripts-status-buttons">
                        <button 
                          type="button"
                          className={`status-filter-btn ${scriptStatusFilter === 'complete' ? 'active' : ''}`}
                          onClick={() => setScriptStatusFilter('complete')}
                        >
                          Complete
                        </button>
                        {hasPendingForCampaign && (
                          <button 
                            type="button"
                            className={`status-filter-btn ${scriptStatusFilter === 'pending' ? 'active' : ''}`}
                            onClick={() => setScriptStatusFilter('pending')}
                          >
                            Pending approval
                          </button>
                        )}
                          <button 
                          type="button"
                          className={`status-filter-btn ${scriptStatusFilter === 'drafts' ? 'active' : ''}`}
                          onClick={() => setScriptStatusFilter('drafts')}
                          disabled={!hasDraftScriptsForExpandedCampaign}
                          >
                            Drafts
                          </button>
                      </div>
                    </div>
                  </div>

                  <div className="scripts-cards-grid" ref={scriptsGridRef} onScroll={checkScriptScrollButtons}>
                    {scriptsLoading ? (
                      <div className="loading-state">
                        <div className="loading-spinner"></div>
                        <p>Loading scripts...</p>
                      </div>
                    ) : getFilteredScripts().length === 0 ? (
                      briefsForExpandedCampaign.length === 0 ? (
                        <div className="empty-state-subtle">
                          <div className="empty-state-content">
                            <div className="empty-state-icon-small">
                              <FileText size={32} />
                            </div>
                            <h4>No scripts yet</h4>
                            <p>Create a brief first to start adding scripts</p>
                          </div>
                        </div>
                      ) : (
                        <div className="empty-state-prominent">
                          <div className="empty-state-content">
                            <div className="empty-state-icon-large">
                              <FileText size={64} />
                            </div>
                            <h3>Create Your First Script</h3>
                            <p>Define the content structure and messaging for your campaign with a detailed script</p>
                            <button 
                              className="empty-state-primary-btn"
                              onClick={handleAddScriptClick}
                            >
                              <Plus size={20} />
                              Create Script
                            </button>
                          </div>
                        </div>
                      )
                    ) : (
                      getFilteredScripts()
                        .sort((a, b) => {
                          const dateA = new Date(a.createdAt || a.created || 0);
                          const dateB = new Date(b.createdAt || b.created || 0);
                          return dateB - dateA; // Newest first (most recent)
                        })
                        .map((script, index) => {
                          const costInfo = calculateScriptCost(script);
                          return (
                            <div key={script.id} className={`modern-script-card ${script.isDraft ? 'script-card-draft' : ''}`} onClick={() => handleScriptClick(script)}>
                              {/* Image Section */}
                              <div className="modern-script-card-image">
                                {expandedCampaign?.avatar || expandedCampaign?.profileImage ? (
                                  <img 
                                    src={expandedCampaign.avatar || expandedCampaign.profileImage} 
                                    alt="Campaign" 
                                    className="script-campaign-image" 
                                  />
                                ) : (
                                  <div className="script-placeholder-image">
                                    <div className="script-placeholder-content">
                                      <FileText size={32} />
                                      <span>Script</span>
                                    </div>
                                  </div>
                                )}
                                
                                {/* Draft Badge */}
                                {script.isDraft && (
                                  <div className="script-draft-tag">
                                    Draft
                                  </div>
                                )}
                                {/* Pending approval badge - admin-added script awaiting brand approval */}
                                {!script.isDraft && script.approvalStatus !== 'approved' && script.approvalStatus !== 'rejected' && (script.approvalStatus === 'pending_brand_approval' || script.addedBy === 'admin') && (
                                  <div className="script-pending-tag">
                                    Pending
                                  </div>
                                )}
                              </div>
                              
                              {/* Content Section */}
                              <div className="modern-script-card-content">
                                <div className="modern-script-card-text">
                                  <h5 className="modern-script-card-title">
                                    {script.name || script.title || `Script ${script.id}`}
                                  </h5>
                                </div>
                                
                                {/* Tags Section */}
                                <div className="modern-script-card-tags">
                                  <div className="modern-script-tag platform-tag">
                                    <img 
                                      src={`/social icons/${(script.settings?.platform || 'tiktok').toLowerCase()}.svg`} 
                                      alt={script.settings?.platform || 'TikTok'} 
                                      style={{ width: '10px', height: '10px', marginRight: '2px' }}
                                    />
                                    <span>{script.settings?.platform || 'TikTok'}</span>
                                  </div>
                                  <div className="modern-script-tag length-tag">
                                    <span>{script.settings?.videoLength || 30}s</span>
                                  </div>
                                  <div className={`modern-script-tag creative-control-tag creative-control-${costInfo.creativeControl}`}>
                                    <span>{costInfo.creativeControl === 'scripted' ? 'Scripted' : 'Creative'}</span>
                                  </div>
                                  {(costInfo.additionalHooks > 0 || costInfo.additionalCTAs > 0) && (
                                    <div className="modern-script-tag extras-tag">
                                      <span className="extras-icon">✨</span>
                                      <span>
                                        {costInfo.additionalHooks > 0 && `+${costInfo.additionalHooks} hooks`}
                                        {costInfo.additionalHooks > 0 && costInfo.additionalCTAs > 0 && ', '}
                                        {costInfo.additionalCTAs > 0 && `+${costInfo.additionalCTAs} CTAs`}
                                      </span>
                                    </div>
                                  )}
                                  <div className="modern-script-tag creators-tag">
                                    <Users size={10} />
                                    <span>
                                      {(() => {
                                        // Count creators assigned to this specific script in the campaign (excluding awaiting_approval, rejected, and completed)
                                        if (!expandedCampaign || !script.id) return 0;
                                        
                                        const campaignBriefsForScript = campaignBriefs.filter(b => b.campaignId === expandedCampaign.id);
                                        const scriptIdToMatch = String(script.id);
                                        const activeCreatorIds = new Set();
                                        
                                        campaignBriefsForScript.forEach(brief => {
                                          if (!brief.creators || !Array.isArray(brief.creators)) return;
                                          
                                          brief.creators.forEach(creator => {
                                            const status = creator.status;
                                            // Only include creators who have accepted (exclude awaiting_approval, rejected, and completed)
                                            if (status && 
                                                status !== 'awaiting_approval' &&
                                                status !== 'rejected' &&
                                                status !== 'completed' &&
                                                status !== 'cancelled') {
                                              
                                              // Check if creator is assigned to this script
                                              const creatorScriptIds = creator.scriptIds || [];
                                              const isAssignedToScript = creatorScriptIds.some(id => String(id) === scriptIdToMatch);
                                              
                                              if (isAssignedToScript) {
                                                activeCreatorIds.add(creator.id);
                                              }
                                            }
                                          });
                                        });
                                        
                                        return activeCreatorIds.size;
                                      })()} creators
                                    </span>
                                  </div>
                                </div>
                                
                                {/* Cost and Date */}
                                <div className="modern-script-card-footer">
                                  {costInfo.additionalCost > 0 && (
                                  <div className="modern-script-card-cost">
                                    <span className="cost-amount">
                                        +{costInfo.additionalCost.toFixed(2)} credit{costInfo.additionalCost !== 1 ? 's' : ''}
                                    </span>
                                  </div>
                                  )}
                                  <div className="modern-script-card-date">
                                    {(() => {
                                      const date = script.createdAt || script.created || new Date().toISOString();
                                      const createdDate = new Date(date);
                                      return createdDate.toLocaleDateString('en-US', { 
                                        month: 'short', 
                                        day: 'numeric',
                                        year: 'numeric'
                                      });
                                    })()}
                                  </div>
                                </div>

                                {/* Add Creator Button - Only show when not a draft */}
                                {!script.isDraft && (
                                  <div className="modern-script-card-actions">
                                    <button
                                      className="script-add-creator-btn"
                                      onClick={(e) => {
                                        e.stopPropagation(); // Prevent card click
                                        // Find the brief that contains this script
                                        const scriptBrief = campaignBriefs.find(brief => {
                                          if (!brief.scripts || !Array.isArray(brief.scripts)) return false;
                                          return brief.scripts.some(s => String(s.id) === String(script.id));
                                        });
                                        
                                        // Navigate to Find Creators with script preselected
                                        setTimeout(() => {
                                          setActiveTab('findCreators', { 
                                            preselectScript: script.id,
                                            preselectBrief: scriptBrief?.id || null,
                                            preselectCampaign: script.campaignId || expandedCampaign?.id || ''
                                          });
                                        }, 100);
                                      }}
                                    >
                                      <Users size={14} />
                                      Add Creator
                                    </button>
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })
                    )}
                  </div>
                  
                  {/* Script Scroll Controls - Only show when campaign is expanded */}
                  {expandedCampaign && (
                    <div className="scripts-scroll-controls">
                      {console.log('Rendering script scroll controls:', { canScrollLeftScripts, canScrollRightScripts })}
                      {(() => {
                        // Check scroll buttons when rendering
                        setTimeout(() => checkScriptScrollButtons(), 100);
                        return null;
                      })()}
                      {canScrollLeftScripts && (
                        <button 
                          className="scripts-scroll-btn"
                          onClick={() => scrollScripts('left')}
                        >
                          <ChevronLeft size={20} />
                        </button>
                      )}
                      {canScrollRightScripts && (
                        <button 
                          className="scripts-scroll-btn"
                          onClick={() => scrollScripts('right')}
                        >
                          <ChevronRight size={20} />
                        </button>
                      )}
                    </div>
                  )}
                </div>

                {/* Creators Section */}
                <div className="detail-section detail-section-creators" ref={creatorsSectionRef}>
                  <div className="detail-section-header">
                    <div className="detail-section-title-group">
                      <h4 className="detail-section-title">Creators</h4>
            </div>
                    <button 
                      className="add-creator-button"
                      onClick={() => {
                        if (setActiveTab) setActiveTab('findCreators');
                      }}
                    >
                      <Plus size={16} />
                      Add Creator
                    </button>
            </div>

                  <div className="creators-sections">
                    {/* Proposed Creators Section - admin-proposed, awaiting brand confirmation */}
                    {proposedCreators.length > 0 && (
                      <div className="creators-section proposed-creators-section">
                        <div className="creators-section-header">
                          <h5 className="creators-section-title">Proposed Creators</h5>
                          <span className="creators-section-count">{proposedCreators.length} awaiting your approval</span>
                        </div>
                        <div className="creators-table">
                          {proposedCreators.map((creator, index) => (
                            <div
                              key={creator.projectId || `${creator.id}-${creator.briefId}-${index}`}
                              className="creator-row creator-row-proposed"
                              onClick={() => {
                                setSelectedProposedCreatorForModal(creator);
                                setShowProposedCreatorModal(true);
                              }}
                              role="button"
                              tabIndex={0}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  setSelectedProposedCreatorForModal(creator);
                                  setShowProposedCreatorModal(true);
                                }
                              }}
                              style={{ cursor: 'pointer' }}
                            >
                              <div className="campaign-detail-creator-avatar active">
                                <img
                                  src={creator.avatar || creator.profileImage || '/creatorimages/creatorpic1v2-min.png'}
                                  alt={creator.name}
                                  className="campaign-detail-creator-image active"
                                  onError={(e) => { e.target.src = '/creatorimages/creatorpic1v2-min.png'; }}
                                />
                              </div>
                              <div className="creator-details">
                                <div className="creator-name">{creator.handle || creator.name}</div>
                                <div className="creator-meta">{creator.name}</div>
                                <div className="creator-assignment-meta">
                                  <span className="creator-brief-name">{creator.briefTitle || 'Brief'}</span>
                                  {creator.scriptIds?.length > 0 && (
                                    <span className="creator-script-info"> • {creator.scriptIds.length} script{creator.scriptIds.length !== 1 ? 's' : ''}</span>
                                  )}
                                </div>
                              </div>
                              <div className="creator-proposed-actions">
                                <div className="creator-proposed-actions-inner" onClick={(e) => e.stopPropagation()}>
                                  <button
                                    type="button"
                                    className="creator-confirm-btn"
                                    onClick={() => handleConfirmProposal(creator)}
                                  >
                                    Confirm
                                  </button>
                                  <button
                                    type="button"
                                    className="creator-reject-btn"
                                    onClick={() => {
                                      setCreatorToRejectProposal(creator);
                                      setShowRejectProposalModal(true);
                                    }}
                                  >
                                    Reject
                                  </button>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Currently Working Section */}
                    <div className="creators-section">
                      <div className="creators-section-header">
                        <h5 className="creators-section-title">Currently Working</h5>
                        <span className="creators-section-count">
                          {(() => {
                            const workingFiltered = currentlyWorkingCreators.filter(c => c.status !== 'pending_brand_approval');
                            const filtered = workingFiltered.filter(creator => {
                              if (creatorStatusFilter === 'all') return true;
                              if (creatorStatusFilter === 'accepted') {
                                return creator.status && 
                                       creator.status !== 'awaiting_approval' &&
                                       creator.status !== 'rejected' &&
                                       creator.status !== 'cancelled' &&
                                       creator.status !== 'completed';
                              }
                              if (creatorStatusFilter === 'awaiting_approval') {
                                return creator.status === 'awaiting_approval';
                              }
                              return true;
                            });
                            return filtered.length;
                          })()} creators
                        </span>
                      </div>
                      <div className="dashboard-scripts-status-filter">
                        <span>Status</span>
                        <div className="dashboard-scripts-status-buttons">
                          <button
                            type="button"
                            className={`status-filter-btn ${creatorStatusFilter === 'all' ? 'active' : ''}`}
                            onClick={() => setCreatorStatusFilter('all')}
                          >
                            All
                          </button>
                          <button
                            type="button"
                            className={`status-filter-btn ${creatorStatusFilter === 'accepted' ? 'active' : ''}`}
                            onClick={() => setCreatorStatusFilter('accepted')}
                          >
                            Accepted
                          </button>
                          <button
                            type="button"
                            className={`status-filter-btn ${creatorStatusFilter === 'awaiting_approval' ? 'active' : ''}`}
                            onClick={() => setCreatorStatusFilter('awaiting_approval')}
                          >
                            Awaiting Approval
                          </button>
                        </div>
                      </div>
                      {(() => {
                        const workingFiltered = currentlyWorkingCreators.filter(c => c.status !== 'pending_brand_approval');
                        const filteredCreators = workingFiltered.filter(creator => {
                          if (creatorStatusFilter === 'all') return true;
                          if (creatorStatusFilter === 'accepted') {
                            return creator.status && 
                                   creator.status !== 'awaiting_approval' &&
                                   creator.status !== 'rejected' &&
                                   creator.status !== 'cancelled' &&
                                   creator.status !== 'completed';
                          }
                          if (creatorStatusFilter === 'awaiting_approval') {
                            return creator.status === 'awaiting_approval';
                          }
                          return true;
                        });

                        return filteredCreators.length === 0 ? (
                        <div className="empty-state-subtle">
                          <div className="empty-state-content">
                            <div className="empty-state-icon-small">
                              <Users size={32} />
                            </div>
                              <h4>No Creators {creatorStatusFilter === 'accepted' ? 'Accepted' : creatorStatusFilter === 'awaiting_approval' ? 'Awaiting Approval' : 'Working'}</h4>
                              <p>{creatorStatusFilter === 'accepted' ? 'No creators have accepted yet' : creatorStatusFilter === 'awaiting_approval' ? 'No creators awaiting approval' : 'Assign creators to briefs to see them here'}</p>
                          </div>
                        </div>
                      ) : (
                        <div className="creators-scroll-container" ref={creatorsGridRef} onScroll={checkCreatorsScrollButtons}>
                          <div className="creators-table">
                              {filteredCreators.map((creator, index) => (
                              <div 
                                key={creator.projectId || `${creator.id}-${creator.briefId}-${index}`}
                                className={`creator-row ${creator.status === 'cancelled' ? 'creator-row-cancelled' : 'clickable'}`}
                                onClick={() => {
                                  if (creator.status === 'cancelled') return;

                                  const { dueText, isOverdue } = getCreatorDueInfo(creator);
                                  
                                  // Check all possible locations for shipping details
                                  const deliveryAddress = creator.deliveryAddress 
                                  || creator.progress?.deliveryAddress 
                                  || (creator.shippingDetails ? {
                                    addressLine1: creator.shippingDetails.addressLine1,
                                    addressLine2: creator.shippingDetails.addressLine2 || '',
                                    city: creator.shippingDetails.city,
                                    state: creator.shippingDetails.state || '',
                                    postalCode: creator.shippingDetails.postalCode,
                                    country: creator.shippingDetails.country,
                                    contactPerson: creator.shippingDetails.contactPerson || creator.name,
                                    phone: creator.shippingDetails.phone || '',
                                    specialInstructions: creator.shippingDetails.specialInstructions || ''
                                  } : null);
                                
                                const rawProjectId = creator.projectId
                                  || creator.progress?.projectId
                                  || null;
                                const normalizedProjectId = ProjectIdGenerator.normalizeProjectId(rawProjectId) || rawProjectId || null;
                                
                                setSelectedCreatorForProgress({
                                    id: creator.id,
                                    creator: creator.handle || creator.name,
                                    name: creator.name,
                                    avatar: creator.avatar || creator.profileImage || '/creatorimages/creatorpic1v2-min.png',
                                    status: creator.status || 'assigned',
                                    tier: creator.tier,
                                    campaignId: expandedCampaign?.id,
                                    campaignName: expandedCampaign?.name,
                                    briefId: creator.briefId,
                                    briefName: creator.briefTitle,
                                    scriptIds: creator.scriptIds || [],
                                    scriptCount: creator.scriptIds ? creator.scriptIds.length : 0,
                                    due: dueText,
                                    isOverdue: isOverdue,
                                    assignedAt: creator.assignedAt,
                                    deadline: creator.deadline,
                                    progress: creator.progress || {
                                      productReceived: false,
                                      filming: false,
                                      editing: false,
                                      submitted: false
                                    },
                                    graceRequest: creator.graceRequest || null,
                                    extendedDeadline: creator.extendedDeadline || null,
                                    deliveryAddress: deliveryAddress,
                                    productType: creator.productType || (creator.serviceOrProduct === 'Product' ? 'product' : 'service'),
                                    serviceOrProduct: creator.serviceOrProduct,
                                    projectId: normalizedProjectId
                                  });
                                  setShowCreatorProgress(true);
                                }}
                              >
                                {creator.status === 'cancelled' && isCancelledRefundEligible(creator) && (
                                  <button
                                    type="button"
                                    className="remove-creator-btn-top-corner"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleRemoveRejectedCreator(
                                        creator,
                                        `Remove this cancelled assignment for ${creator.name || creator.handle}? Credits will be refunded to your account.`
                                      );
                                    }}
                                    title="Remove cancelled assignment"
                                  >
                                    <X size={14} />
                                  </button>
                                )}
                                <div className="campaign-detail-creator-avatar active">
                                  <img 
                                    src={creator.avatar || creator.profileImage || '/creatorimages/creatorpic1v2-min.png'} 
                                    alt={creator.name} 
                                    className="campaign-detail-creator-image active"
                                    onError={(e) => { e.target.src = '/creatorimages/creatorpic1v2-min.png'; }} 
                                  />
                                </div>
                                <div className="creator-details">
                                  <div className="creator-name">{creator.handle || creator.name}</div>
                                  <div className="creator-meta">{creator.name}</div>
                                  {(() => {
                                    // Get brief name and script info
                                    const brief = campaignBriefs.find(b => b.id === creator.briefId && b.campaignId === expandedCampaign?.id);
                                    const briefName = brief?.title || brief?.name || creator.briefTitle || 'No brief';
                                    const scriptIds = creator.scriptIds || [];
                                    
                                    if (brief && brief.scripts && scriptIds.length > 0) {
                                      // Get script names from the brief
                                      const scriptNames = brief.scripts
                                        .filter(s => scriptIds.some(id => String(s.id) === String(id)))
                                        .map(s => s.name || s.title || s.scriptName || 'Untitled Script');
                                      
                                      if (scriptNames.length > 0) {
                                        const firstScriptName = scriptNames[0];
                                        const additionalCount = scriptNames.length - 1;
                                        
                                        return (
                                          <div className="creator-assignment-meta">
                                            <span className="creator-brief-name">{briefName}</span>
                                            {additionalCount > 0 ? (
                                              <span className="creator-script-info"> +{additionalCount}</span>
                                            ) : (
                                              <span className="creator-script-info"> • {firstScriptName}</span>
                                            )}
                                          </div>
                                        );
                                      }
                                    }
                                    
                                    // Fallback: just show brief name if available
                                    if (briefName && briefName !== 'No brief') {
                                      return (
                                        <div className="creator-assignment-meta">
                                          <span className="creator-brief-name">{briefName}</span>
                                        </div>
                                      );
                                    }
                                    
                                    return null;
                                  })()}
                                  {/* Review Content Button - When submission exists */}
                                  {creator.hasSubmission && creator.submissionId && (
                                    <button 
                                      className="review-content-btn"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        // Navigate to Hand-In page and auto-open submission review
                                        setActiveTab('handin', { 
                                          openSubmissionId: creator.submissionId,
                                          openCampaignId: expandedCampaign?.id,
                                          openBriefId: creator.briefId
                                        });
                                      }}
                                    >
                                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                        <polyline points="14,2 14,8 20,8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                        <line x1="16" y1="13" x2="8" y2="13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                        <line x1="16" y1="17" x2="8" y2="17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                        <polyline points="10,9 9,9 8,9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                      </svg>
                                      Review Content
                                    </button>
                                  )}
                                  {/* Extension Request Button - Under Creator Name */}
                                  {creator.graceRequest && creator.graceRequest.status === 'pending' && (
                                    <button 
                                      className="extension-request-btn"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        const rawProjectId = creator.projectId
                                          || creator.progress?.projectId
                                          || null;
                                        const normalizedProjectId = ProjectIdGenerator.normalizeProjectId(rawProjectId) || rawProjectId || null;
                                        setSelectedCreatorForProgress({
                                          ...creator,
                                          campaignId: expandedCampaign?.id,
                                          briefId: creator.briefId,
                                          briefName: creator.briefTitle,
                                          campaignName: expandedCampaign?.name,
                                          progress: creator.progress || {
                                            accepted: true,
                                            productReceived: false,
                                            filming: false,
                                            editing: false,
                                            submitted: false
                                          },
                                          graceRequest: creator.graceRequest || null,
                                          extendedDeadline: creator.extendedDeadline || null,
                                          openExtensionRequest: true,
                                          projectId: normalizedProjectId
                                        });
                                        setShowCreatorProgress(true);
                                      }}
                                    >
                                      <Clock size={12} />
                                      Extension Requested
                                    </button>
                                  )}
                                  {/* Cancellation Request Button - Under Creator Name */}
                                  {creator.cancellationRequest && creator.cancellationRequest.status === 'pending' && (
                                    <button 
                                      className="cancellation-request-btn"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        const rawProjectId = creator.projectId
                                          || creator.progress?.projectId
                                          || null;
                                        const normalizedProjectId = ProjectIdGenerator.normalizeProjectId(rawProjectId) || rawProjectId || null;
                                        setSelectedCreatorForCancellation({
                                          ...creator,
                                          campaignId: expandedCampaign?.id,
                                          briefId: creator.briefId,
                                          briefName: creator.briefTitle,
                                          campaignName: expandedCampaign?.name,
                                          cancellationRequest: creator.cancellationRequest || null,
                                          projectId: normalizedProjectId
                                        });
                                        setShowCancellationRequestModal(true);
                                      }}
                                    >
                                      <XCircle size={12} />
                                      Cancellation Requested
                                    </button>
                                  )}
                                  {/* Shipping Details Button - Under Creator Name */}
                                  {(() => {
                                    // Check all possible locations for shipping details
                                    const deliveryAddress = creator.deliveryAddress 
                                      || creator.progress?.deliveryAddress 
                                      || (creator.shippingDetails ? {
                                        addressLine1: creator.shippingDetails.addressLine1,
                                        addressLine2: creator.shippingDetails.addressLine2 || '',
                                        city: creator.shippingDetails.city,
                                        state: creator.shippingDetails.state || '',
                                        postalCode: creator.shippingDetails.postalCode,
                                        country: creator.shippingDetails.country,
                                        contactPerson: creator.shippingDetails.contactPerson || creator.name,
                                        phone: creator.shippingDetails.phone || '',
                                        specialInstructions: creator.shippingDetails.specialInstructions || ''
                                      } : null);
                                    const isProduct = creator.productType === 'product' || creator.serviceOrProduct === 'Product';
                                    return isProduct && deliveryAddress && creator.status !== 'product_sent';
                                  })() && (
                                    <button 
                                      className="shipping-details-btn"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        // Check all possible locations for shipping details
                                        const deliveryAddress = creator.deliveryAddress 
                                          || creator.progress?.deliveryAddress 
                                          || (creator.shippingDetails ? {
                                            addressLine1: creator.shippingDetails.addressLine1,
                                            addressLine2: creator.shippingDetails.addressLine2 || '',
                                            city: creator.shippingDetails.city,
                                            state: creator.shippingDetails.state || '',
                                            postalCode: creator.shippingDetails.postalCode,
                                            country: creator.shippingDetails.country,
                                            contactPerson: creator.shippingDetails.contactPerson || creator.name,
                                            phone: creator.shippingDetails.phone || '',
                                            specialInstructions: creator.shippingDetails.specialInstructions || ''
                                          } : null);
                                        const rawProjectId = creator.projectId
                                          || creator.progress?.projectId
                                          || null;
                                        const normalizedProjectId = ProjectIdGenerator.normalizeProjectId(rawProjectId) || rawProjectId || null;
                                        setSelectedCreatorForProgress({
                                          ...creator,
                                          campaignId: expandedCampaign?.id,
                                          briefId: creator.briefId,
                                          briefName: creator.briefTitle,
                                          campaignName: expandedCampaign?.name,
                                          progress: creator.progress || {
                                            accepted: true,
                                            productReceived: false,
                                            filming: false,
                                            editing: false,
                                            submitted: false
                                          },
                                          deliveryAddress: deliveryAddress,
                                          productType: creator.productType || (creator.serviceOrProduct === 'Product' ? 'product' : 'service'),
                                          serviceOrProduct: creator.serviceOrProduct,
                                          graceRequest: creator.graceRequest || null,
                                          extendedDeadline: creator.extendedDeadline || null,
                                          openShippingDetails: true,
                                          projectId: normalizedProjectId
                                        });
                                        setShowCreatorProgress(true);
                                      }}
                                    >
                                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                        <polyline points="3.27,6.96 12,12.01 20.73,6.96" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                        <line x1="12" y1="22.08" x2="12" y2="12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                      </svg>
                                      Shipping Details
                                    </button>
                                  )}
                                </div>
                                <div className="creator-assignment-info">
                                  <div className="assignment-info-row">
                                    <div className="assignment-info-item">
                                      <span className="assignment-info-label">Days Left</span>
                                      <span className="assignment-info-value days-left">
                                        {getCreatorDueInfo(creator).dueText}
                                      </span>
                                    </div>
                                    <div className="assignment-info-item">
                                      <span className="assignment-info-label">Status</span>
                                      <span className={`assignment-info-value status-indicator ${getCreatorStatusCssClass(creator)}`}>
                                        {getCreatorStatusLabel(creator)}
                                      </span>
                                    </div>
                                  </div>
                                  {creator.status === 'cancelled' ? (
                                    <p className="creator-cancelled-hint">
                                      {isCancelledRefundEligible(creator)
                                        ? 'Assignment cancelled — refund removal available'
                                        : 'Assignment cancelled — refund removal is locked until cancellation is approved/verified'}
                                    </p>
                                  ) : creator.status === 'rejected' ? (
                                    <button 
                                      className="creator-remove-btn"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleRemoveRejectedCreator(creator);
                                      }}
                                    >
                                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <polyline points="3 6 5 6 21 6"></polyline>
                                        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                                      </svg>
                                      Remove & Refund Credits
                                    </button>
                                  ) : creator.status === 'awaiting_approval' && isAcceptExpired(creator) ? (
                                    <button 
                                      className="creator-remove-btn"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleRemoveRejectedCreator(
                                          creator,
                                          `${creator.name} didn't respond within 2 days. Remove this assignment and refund credits?`
                                        );
                                      }}
                                    >
                                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <polyline points="3 6 5 6 21 6"></polyline>
                                        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                                      </svg>
                                      Remove & Refund (expired)
                                    </button>
                                  ) : (
                                    <div className="creator-actions-buttons">
                                      <button 
                                        className={`creator-chat-btn ${!['accepted', 'confirmed', 'awaiting_product', 'product_sent', 'filming', 'editing', 'submitted', 'needs_revision', 'in_progress'].includes(creator.status) ? 'disabled' : ''}`}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          if (['accepted', 'confirmed', 'awaiting_product', 'product_sent', 'filming', 'editing', 'submitted', 'needs_revision', 'in_progress'].includes(creator.status)) {
                                            // Find brief info from campaignBriefs
                                            const brief = campaignBriefs.find(b => b.id === creator.briefId && b.campaignId === expandedCampaign?.id);
                                            setSelectedCreatorForProgress({
                                              ...creator,
                                              campaignId: expandedCampaign?.id || creator.campaignId,
                                              campaignName: expandedCampaign?.name || creator.campaignName,
                                              briefId: creator.briefId,
                                              briefName: creator.briefTitle || brief?.title || brief?.name || creator.briefName,
                                              scriptCount: creator.scriptIds?.length || creator.scriptCount || 0,
                                              scriptIds: creator.scriptIds || []
                                            });
                                            setShowCreatorProgress(true);
                                          }
                                        }}
                                        disabled={!['accepted', 'confirmed', 'awaiting_product', 'product_sent', 'filming', 'editing', 'submitted', 'needs_revision', 'in_progress'].includes(creator.status)}
                                      >
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
                                        </svg>
                                        {creator.status === 'awaiting_approval' ? 'Chat (Awaiting Accept)' : 'Chat'}
                                        {(() => {
                                          const normalizedProjectId = creator.projectId ? ProjectIdGenerator.normalizeProjectId(creator.projectId) : null;
                                          const chat = normalizedProjectId ? getChatForProject(normalizedProjectId) : null;
                                          const unreadCount = chat ? getUnreadCount(chat.chatId) : 0;
                                          return unreadCount > 0 ? (
                                            <span className="chat-unread-badge">{unreadCount}</span>
                                          ) : null;
                                        })()}
                                      </button>
                                    </div>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                        );
                      })()}
                      
                      {/* Creators Scroll Controls - Only show when campaign is expanded */}
                      {expandedCampaign && (() => {
                        const filteredCreators = currentlyWorkingCreators.filter(creator => {
                          if (creatorStatusFilter === 'all') return true;
                          if (creatorStatusFilter === 'accepted') {
                            return creator.status && 
                                   creator.status !== 'awaiting_approval' &&
                                   creator.status !== 'rejected' &&
                                   creator.status !== 'cancelled' &&
                                   creator.status !== 'completed';
                          }
                          if (creatorStatusFilter === 'awaiting_approval') {
                            return creator.status === 'awaiting_approval';
                          }
                          return true;
                        });
                        
                        return filteredCreators.length > 0 ? (
                          <div className="creators-scroll-controls">
                            {(() => {
                              // Check scroll buttons when rendering
                              setTimeout(() => checkCreatorsScrollButtons(), 100);
                              return null;
                            })()}
                            {canScrollLeftCreators && (
                              <button 
                                className="creators-scroll-btn"
                                onClick={() => scrollCreators('left')}
                              >
                                <ChevronLeft size={20} />
                              </button>
                            )}
                            {canScrollRightCreators && (
                              <button 
                                className="creators-scroll-btn"
                                onClick={() => scrollCreators('right')}
                              >
                                <ChevronRight size={20} />
                              </button>
                            )}
                          </div>
                        ) : null;
                      })()}
                    </div>

                    {/* Shortlisted Creators Section */}
                    <div className="creators-section">
                      <div className="creators-section-header">
                        <h5 className="creators-section-title">Shortlisted Creators</h5>
                        <span className="creators-section-count">{campaignShortlistedCreators.length} creators</span>
                      </div>
                      {campaignShortlistedCreators.length === 0 ? (
                        <div className="empty-state-subtle">
                          <div className="empty-state-content">
                            <div className="empty-state-icon-small">
                              <Users size={32} />
                            </div>
                            <h4>No Creators Shortlisted</h4>
                            <p>Add creators to this campaign to start collaborating</p>
                          </div>
                        </div>
                      ) : (
                        <div className="creators-scroll-container">
                          <div className="creators-table">
                            {campaignShortlistedCreators.map((creator) => (
                              <div 
                                key={creator.id}
                                className="creator-row clickable shortlisted-creator-card"
                                onClick={() => {
                                  // Navigate to Find Creators and auto-open this creator's portfolio
                                  if (setActiveTab) {
                                    setActiveTab('findCreators', { openCreatorId: creator.id });
                                  }
                                }}
                              >
                                <button
                                  className="remove-creator-btn-top-corner"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleRemoveCreatorFromShortlist(creator);
                                  }}
                                  title="Remove from shortlist"
                                >
                                  <Trash2 size={14} />
                                </button>
                                <div className="campaign-detail-creator-avatar">
                                  <img 
                                    src={creator.avatar || creator.profileImage || '/creatorimages/creatorpic1v2-min.png'} 
                                    alt={creator.name} 
                                    className="campaign-detail-creator-image"
                                    onError={(e) => { e.target.src = '/creatorimages/creatorpic1v2-min.png'; }} 
                                  />
                                </div>
                                <div className="creator-details">
                                  <div className="creator-name">{creator.handle || creator.name}</div>
                                  <div className="creator-meta">{creator.name}</div>
                                </div>
                                <div className="creator-actions-row">
                                  <button
                                    className="assign-creator-btn"
                                    onClick={async (e) => {
                                      e.stopPropagation();
                                      
                                      // Check credits before opening assign modal
                                      try {
                                        const credits = await CreditService.getBalance();
                                        setCurrentCredits(credits);
                                        
                                        if (credits <= 0) {
                                          // No credits - open credit purchase modal instead
                                          setShowCreditPurchaseModal(true);
                                        } else {
                                          // Has credits - open assign modal
                                          setCreatorToAssign(creator);
                                          setShowAssignCreatorModal(true);
                                        }
                                      } catch (error) {
                                        console.error('Error checking credits:', error);
                                        // If error, still allow opening assign modal (it will check credits there)
                                        setCreatorToAssign(creator);
                                        setShowAssignCreatorModal(true);
                                      }
                                    }}
                                    title="Assign to brief/script"
                                  >
                                    Assign
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Recently Completed Section */}
                    <div className="creators-section">
                      <div className="creators-section-header">
                        <h5 className="creators-section-title">Recently Completed</h5>
                        <span className="creators-section-count">{completedCreators.length} creator{completedCreators.length !== 1 ? 's' : ''}</span>
                      </div>
                      {completedCreators.length === 0 ? (
                        <div className="empty-state-subtle">
                          <div className="empty-state-content">
                            <div className="empty-state-icon-small">
                              <Play size={32} />
                            </div>
                            <h4>No Recently Completed Videos</h4>
                            <p>Creators who complete videos will appear here</p>
                          </div>
                        </div>
                      ) : (
                        <div className="completed-creators-grid">
                          {completedCreators.slice(0, 4).map((creator) => (
                            <div
                              key={creator.id}
                              className="completed-creator-card"
                              onClick={async () => {
                                // Fetch the submission data to open in ApprovedContentModal
                                const auth = getAuth();
                                const user = auth.currentUser;
                                if (!user || !expandedCampaign || !creator.briefId || !creator.projectId) return;
                                
                                try {
                                  // Find the submission by projectId
                                  const submissionsRef = collection(
                                    db,
                                    'users',
                                    user.uid,
                                    'campaigns',
                                    expandedCampaign.id,
                                    'briefs',
                                    creator.briefId,
                                    'submissions'
                                  );
                                  
                                  const submissionsSnapshot = await getDocs(submissionsRef);
                                  const submissionDoc = submissionsSnapshot.docs.find(doc => {
                                    const data = doc.data();
                                    const submissionProjectId = data.projectId ? 
                                      (ProjectIdGenerator.normalizeProjectId(data.projectId) || data.projectId) : null;
                                    return submissionProjectId && (
                                      submissionProjectId === creator.projectId ||
                                      ProjectIdGenerator.areEquivalent(submissionProjectId, creator.projectId)
                                    );
                                  });
                                  
                                  if (submissionDoc) {
                                    const submissionData = submissionDoc.data();
                                    const submission = {
                                      id: submissionDoc.id,
                                      ...submissionData,
                                      creator: {
                                        name: creator.name,
                                        handle: creator.handle,
                                        id: creator.id
                                      },
                                      creatorId: creator.id || submissionData.creatorId,
                                      briefId: creator.briefId || submissionData.briefId,
                                      campaignId: expandedCampaign.id || submissionData.campaignId,
                                      campaign: expandedCampaign.name || expandedCampaign.title,
                                      _originalData: {
                                        ...submissionData,
                                        briefId: creator.briefId,
                                        campaignId: expandedCampaign.id
                                      }
                                    };
                                    setSelectedApprovedSubmission(submission);
                                    setShowApprovedModal(true);
                                  }
                                } catch (error) {
                                  console.error('Error fetching submission:', error);
                                }
                              }}
                            >
                              <div className="completed-creator-avatar">
                                <img
                                  src={creator.avatar || creator.profileImage || '/creatorimages/creatorpic1v2-min.png'}
                                  alt={creator.name}
                                  onError={(e) => { e.target.src = '/creatorimages/creatorpic1v2-min.png'; }}
                                />
                              </div>
                              <div className="completed-creator-info">
                                <div className="completed-creator-name">{creator.name}</div>
                                <div className="completed-creator-handle">{creator.handle || creator.creator}</div>
                                <div className="completed-creator-brief">{creator.briefTitle || 'Brief'}</div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

      {/* Modals */}
      {showCreateCampaignModal && (
        <CreateCampaignModal
          onClose={() => setShowCreateCampaignModal(false)}
          onCampaignCreated={handleCampaignCreated}
        />
      )}

      {showEditCampaignModal && selectedCampaign && (
        <EditCampaignModal
          campaign={selectedCampaign}
          onClose={() => setShowEditCampaignModal(false)}
          onSave={updatedFields => {
            setSelectedCampaign(prev => ({ ...prev, ...updatedFields }));
            setShowEditCampaignModal(false);
          }}
        />
      )}

      <CampaignCreatedModal
        isOpen={showCongratsModal}
        onClose={() => setShowCongratsModal(false)}
        campaignName={justCreatedCampaign}
        onCreateBrief={() => {
          setShowCongratsModal(false);
          // Find the newly created campaign and expand it
          const newCampaign = campaigns.find(c => c.name === justCreatedCampaign);
          if (newCampaign) {
            setExpandedCampaign(newCampaign);
            // Skip the add brief modal and go straight to brief creation
            setShowBriefPanel(true);
          }
        }}
      />

      {/* Brief Created Modal */}
      <BriefCreatedModal
        isOpen={showBriefCreatedModal}
        onClose={() => setShowBriefCreatedModal(false)}
        briefName={justCreatedBriefName}
        onCreateScript={() => {
          setShowBriefCreatedModal(false);
          setShowScriptPanel(true);
        }}
      />

      <BriefDetailModal
        brief={selectedBrief}
        isOpen={showBriefDetailModal}
        onClose={() => setShowBriefDetailModal(false)}
        onViewBrief={handleViewBrief}
        onAddScript={handleAddScript}
        onDeleteBrief={handleDeleteBrief}
        onScriptClick={handleScriptClickFromBrief}
        onAddScriptToBrief={(brief) => {
          // Set the brief first and keep it
          setSelectedBrief(brief);
          
          // Close brief modal
          setShowBriefDetailModal(false);
          
          // Go directly to script creation panel
          setTimeout(() => {
            setShowScriptPanel(true);
          }, 100);
        }}
        onCreatorClick={(creator) => {
          // Close brief modal
          setShowBriefDetailModal(false);
          
          // Navigate to creator dashboard tab and pass creator data
          setTimeout(() => {
            setActiveTab('findCreators', { openCreatorId: creator.id });
          }, 100);
        }}
        onAddCreatorToBrief={(brief) => {
          // Close brief modal
          setShowBriefDetailModal(false);
          
          // Navigate to creator dashboard to select and assign creators
          setTimeout(() => {
            setActiveTab('findCreators', { 
              preselectBrief: brief.id,
              preselectCampaign: brief.campaignId 
            });
          }, 100);
        }}
      />

      <ScriptDetailModal
        script={selectedScript}
        isOpen={showScriptDetailModal}
        onClose={() => setShowScriptDetailModal(false)}
        onViewScript={handleViewScript}
        onAddCreator={handleAddCreatorToScript}
        onDeleteScript={handleDeleteScript}
        onApproveScript={handleApproveScript}
        onRejectScript={handleRejectScript}
        campaignBriefs={campaignBriefs}
        campaignId={selectedScript?.campaignId || expandedCampaign?.id}
        onOpenApprovedModal={(submission) => {
          setSelectedApprovedSubmission(submission);
          setShowApprovedModal(true);
        }}
      />

      <ScriptFeedbackModal
        script={selectedScript}
        isOpen={showScriptFeedbackModal}
        onClose={() => {
          setShowScriptFeedbackModal(false);
          setSelectedScript(null);
        }}
        onSubmitFeedback={handleScriptFeedback}
      />

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && selectedCampaign && (
        <div className="delete-confirm-modal">
          <div className="delete-confirm-content">
            <h3>Delete Campaign</h3>
            <p>Are you sure you want to delete "{selectedCampaign.name}"? This action cannot be undone and will remove all associated briefs.</p>
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end', marginTop: '1.5rem' }}>
              <button 
                className="campaign-card-action-btn"
                onClick={() => setShowDeleteConfirm(false)}
                disabled={deleteLoading}
              >
                Cancel
              </button>
              <button 
                className="campaign-card-action-btn"
                onClick={handleDeleteConfirm}
                disabled={deleteLoading}
                style={{ background: '#ef4444', color: 'white', border: 'none' }}
              >
                {deleteLoading ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Archive Confirmation Modal */}
      {showArchiveConfirm && campaignToArchive && (
        <div className="archive-confirm-modal">
          <div className="archive-confirm-content">
            <h3>Archive Campaign</h3>
            <p>Are you sure you want to archive "{campaignToArchive.name}"? Archived campaigns can be restored later.</p>
            <div className="archive-modal-actions">
              <button 
                className="campaign-card-action-btn cancel"
                onClick={() => setShowArchiveConfirm(false)}
              >
                Cancel
              </button>
              <button 
                className="campaign-card-action-btn archive"
                onClick={handleArchiveConfirm}
              >
                Archive Campaign
              </button>
            </div>
          </div>
        </div>
      )}



      <AddScriptToBriefModal
        isOpen={showAddScriptToBriefModal}
        onClose={() => setShowAddScriptToBriefModal(false)}
        onAddScript={handleAddScriptsToBrief}
        brief={selectedBrief}
        campaignScripts={campaignScripts.filter(s => s.campaignId === expandedCampaign?.id)}
      />

      {/* Brief Creation Panel */}
      <ClientBriefPanel
        open={showBriefPanel}
        onClose={() => setShowBriefPanel(false)}
        onBriefSaved={handleBriefSaved}
        onBriefGenerated={handleBriefGenerated}
        initialCampaign={expandedCampaign?.id || ''}
      />

      {/* Brief View/Edit Panel */}
      <ClientBriefPanel
        open={showViewBriefPanel}
        onClose={() => setShowViewBriefPanel(false)}
        brief={viewBriefData}
        startStep={1}
        onBriefSaved={handleBriefSaved}
        onBriefGenerated={handleBriefGenerated}
        initialCampaign={expandedCampaign?.id || ''}
      />


      {/* Script Creation Panel */}
      <ClientScriptPanel
        open={showScriptPanel}
        onClose={() => setShowScriptPanel(false)}
        onScriptSaved={handleScriptSaved}
        initialCampaign={expandedCampaign?.id || ''}
        initialBrief={selectedBrief}
      />

      {/* Script View/Edit Panel */}
      <ClientScriptPanel
        open={showViewScriptPanel}
        onClose={() => setShowViewScriptPanel(false)}
        script={viewScriptData}
        onScriptSaved={handleScriptSaved}
        initialCampaign={viewScriptData?.campaignId || expandedCampaign?.id || ''}
        initialBrief={viewScriptData?.briefId || null}
      />



      {/* Creator Progress Modal */}
      {/* Cancellation Request Modal */}
      {showCancellationRequestModal && selectedCreatorForCancellation && (
        <CancellationRequestModal
          isOpen={showCancellationRequestModal}
          onClose={() => {
            setShowCancellationRequestModal(false);
            setSelectedCreatorForCancellation(null);
          }}
          creatorData={selectedCreatorForCancellation}
          onCancellationResponse={(status) => {
            console.log(`Cancellation request ${status}`);
            setShowCancellationRequestModal(false);
            setSelectedCreatorForCancellation(null);
            // Refresh the page data will happen automatically via listeners
          }}
        />
      )}

      {showCreatorProgress && selectedCreatorForProgress && (
        <CreatorProgressModal
          isOpen={showCreatorProgress}
          onClose={() => setShowCreatorProgress(false)}
          creatorData={selectedCreatorForProgress}
          setActiveTab={setActiveTab}
          onScriptClick={(script) => {
            // Close creator progress modal
            setShowCreatorProgress(false);
            
            // Find the script in campaign briefs
            let foundScript = null;
            for (const brief of campaignBriefs) {
              if (brief.scripts && Array.isArray(brief.scripts)) {
                foundScript = brief.scripts.find(s => String(s.id) === String(script.id));
                if (foundScript) {
                  // Add campaignId and briefId to the script
                  foundScript = {
                    ...foundScript,
                    campaignId: brief.campaignId || expandedCampaign?.id,
                    briefId: brief.id
                  };
                  break;
                }
              }
            }
            
            // If script found, open script detail modal
            if (foundScript) {
              setTimeout(() => {
                setSelectedScript(foundScript);
                setShowScriptDetailModal(true);
              }, 100);
            }
          }}
        />
      )}

      {/* Assign Creator Modal */}
      {showAssignCreatorModal && creatorToAssign && (
        <AssignCreatorModal
          isOpen={showAssignCreatorModal}
          onClose={() => {
            setShowAssignCreatorModal(false);
            setCreatorToAssign(null);
          }}
          creators={[creatorToAssign]}
          onRemoveCreator={() => {}}
          preselectCampaign={expandedCampaign?.id || ''}
          onAssignComplete={(assignments) => {
            console.log('Creator assigned:', assignments);
            setShowAssignCreatorModal(false);
            setCreatorToAssign(null);
          }}
        />
      )}

      {/* Credit Purchase Modal */}
      <CreditPurchaseModal
        isOpen={showCreditPurchaseModal}
        onClose={() => setShowCreditPurchaseModal(false)}
        currentCredits={currentCredits}
        onPurchaseSuccess={() => {
          setShowCreditPurchaseModal(false);
          // Refresh credits after purchase
          CreditService.getBalance().then(setCurrentCredits).catch(console.error);
        }}
      />

      {/* Reject Proposal Feedback Modal */}
      <RejectProposalFeedbackModal
        isOpen={showRejectProposalModal}
        onClose={() => {
          setShowRejectProposalModal(false);
          setCreatorToRejectProposal(null);
        }}
        creator={creatorToRejectProposal}
        onReject={handleRejectProposal}
      />

      {/* Creator Portfolio Modal - for proposed creators */}
      {showProposedCreatorModal && selectedProposedCreatorForModal && (
        <CreatorModal
          creator={{
            ...selectedProposedCreatorForModal,
            profileImage: selectedProposedCreatorForModal.profileImage || selectedProposedCreatorForModal.avatar || selectedProposedCreatorForModal.image || 'https://via.placeholder.com/200',
            niche: selectedProposedCreatorForModal.niche || 'General',
          }}
          onClose={() => {
            setShowProposedCreatorModal(false);
            setSelectedProposedCreatorForModal(null);
          }}
          variant="proposal"
          onConfirm={(creator) => {
            handleConfirmProposal(creator);
            setShowProposedCreatorModal(false);
            setSelectedProposedCreatorForModal(null);
          }}
          onReject={(creator) => {
            setShowProposedCreatorModal(false);
            setSelectedProposedCreatorForModal(null);
            setCreatorToRejectProposal(creator);
            setShowRejectProposalModal(true);
          }}
        />
      )}

      {/* Approved Content Modal */}
      {showApprovedModal && selectedApprovedSubmission && (
        <ApprovedContentModal
          isOpen={showApprovedModal}
          onClose={() => {
            setShowApprovedModal(false);
            setSelectedApprovedSubmission(null);
          }}
          submission={selectedApprovedSubmission}
          onLeaveReview={async (submission, reviewData) => {
            try {
              await SubmissionService.submitReview(submission, reviewData);
              console.log('Review submitted successfully');
            } catch (error) {
              console.error('Error submitting review:', error);
              throw error;
            }
          }}
          onReassignCreator={(submission) => {
            // Close the approved modal
            setShowApprovedModal(false);
            setSelectedApprovedSubmission(null);
            
            // Get creator ID and brief/campaign info from submission for re-assignment
            const creatorId = submission.creatorId || submission.creator?.id || submission.creator?.handle;
            const briefId = submission.briefId || submission._originalData?.briefId;
            const campaignId = expandedCampaign?.id || submission.campaignId || submission._originalData?.campaignId;
            
            // Navigate to find creators tab
            // If creatorId exists, try to open their profile, otherwise just show the panel for assignment
            setTimeout(() => {
              if (creatorId) {
                // Try to open creator profile, but if not found, user can still search and assign
                setActiveTab('findCreators', { 
                  openCreatorId: creatorId,
                  preselectBrief: briefId,
                  preselectCampaign: campaignId
                });
              } else {
                // No creator ID - just show panel with brief/campaign preselected for assignment
                setActiveTab('findCreators', { 
                  preselectBrief: briefId,
                  preselectCampaign: campaignId
                });
              }
            }, 100);
          }}
        />
      )}


      {/* Tooltip */}
      {tooltip.visible && (
        <div 
          className="campaign-tooltip"
          style={{
            position: 'fixed',
            top: `${tooltip.position.top}px`,
            left: `${tooltip.position.left}px`,
            transform: 'translateX(-50%)',
            zIndex: 9999,
          }}
        >
          {tooltip.text}
        </div>
      )}

    </div>
  );
}

export default DashboardCampaigns; 
